import { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "./logo.png";

// ─── Supabase ─────────────────────────────────────────────────────────────────
const SUPABASE_URL  = "https://lbzmwxaghemxmgxunglk.supabase.co";
const SUPABASE_ANON = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imxiem13eGFnaGVteG1neHVuZ2xrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ5NDQ4MzMsImV4cCI6MjA5MDUyMDgzM30.8drHXdnXca7VAIkLsFoMfmK8PEDm7-9G02L_tpnuBc4";

const sb = {
  headers: {
    "apikey": SUPABASE_ANON,
    "Authorization": `Bearer ${SUPABASE_ANON}`,
    "Content-Type": "application/json",
    "Prefer": "return=representation",
    "Accept": "application/json",
  },

  async select(table, filter = "", order = "created.asc") {
    try {
      const orderClause = order ? `&order=${order}` : "";
      const url = `${SUPABASE_URL}/rest/v1/${table}?${filter}${orderClause}`;
      const res = await fetch(url, {
        method: "GET",
        headers: sb.headers,
        mode: "cors",
        cache: "no-cache",
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) { console.error("sb.select error:", e); return null; }
  },

  async upsert(table, data) {
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
        method: "POST",
        headers: { ...sb.headers, "Prefer": "resolution=merge-duplicates,return=representation" },
        body: JSON.stringify(Array.isArray(data) ? data : [data]),
        mode: "cors",
        cache: "no-cache",
      });
      if (!res.ok) { console.error("sb.upsert error:", await res.text()); return null; }
      return await res.json();
    } catch (e) { console.error("sb.upsert error:", e); return null; }
  },

  async update(table, match, data) {
    try {
      const query = Object.entries(match).map(([k,v]) => `${k}=eq.${encodeURIComponent(v)}`).join("&");
      const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?${query}`, {
        method: "PATCH",
        headers: sb.headers,
        body: JSON.stringify(data),
        mode: "cors",
        cache: "no-cache",
      });
      return res.ok;
    } catch (e) { console.error("sb.update error:", e); return false; }
  },

  async delete(table, match) {
    try {
      const query = Object.entries(match).map(([k,v]) => `${k}=eq.${encodeURIComponent(v)}`).join("&");
      const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?${query}`, {
        method: "DELETE",
        headers: sb.headers,
        mode: "cors",
        cache: "no-cache",
      });
      return res.ok;
    } catch (e) { console.error("sb.delete error:", e); return false; }
  },
};

// ─── Session (localStorage with sessionStorage fallback for iOS) ──────────────
const getSession = () => {
  try { return JSON.parse(localStorage.getItem("jgg_session") || sessionStorage.getItem("jgg_session")); } 
  catch { return null; }
};
const setSession = (val) => {
  try {
    if (val) {
      const s = JSON.stringify(val);
      localStorage.setItem("jgg_session", s);
      sessionStorage.setItem("jgg_session", s);
    } else {
      localStorage.removeItem("jgg_session");
      sessionStorage.removeItem("jgg_session");
    }
  } catch {}
};

// ─── Auth ─────────────────────────────────────────────────────────────────────

// ─── Pipeline ─────────────────────────────────────────────────────────────────
const PIPELINE = [
  { step: 1, title: "Initial Email",    priority: "High"   },
  { step: 2, title: "Initial Call",     priority: "High"   },
  { step: 3, title: "Follow-up Call",   priority: "Medium" },
  { step: 4, title: "Follow-up Call 2", priority: "Medium" },
  { step: 5, title: "Final Decision",   priority: "High"   },
];

const getTasksForContact = cid =>
  PIPELINE.map(p => ({ id: `${cid}_step${p.step}`, contactId: cid, ...p }));

// ─── Google Sheets ────────────────────────────────────────────────────────────
const SHEETS_URL = "https://script.google.com/macros/s/AKfycbw9gCpAoPN7FmoXo5N7tAMMCKod5zqpA9TC9LC7CwXeKULzRYWvPqmw033ei9CM_i8E/exec";

async function sendToSheet(contact, outcome) {
  try {
    await fetch(SHEETS_URL, {
      method: "POST", mode: "no-cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sheet:      outcome === "sold" ? "Sold Jobs" : "Lost Jobs",
        first_name: contact.first_name || "",
        last_name:  contact.last_name  || "",
        company:    contact.company    || "",
        email:      contact.email      || "",
        phone:      contact.phone      || "",
        address:    contact.address    || "",
      }),
    });
    return true;
  } catch { return false; }
}


// ─── Slack DM ─────────────────────────────────────────────────────────────────
const SLACK_PROXY = "https://jg-graphics-crm.netlify.app/.netlify/functions/slack";

async function sendCalendarSlack(ev, triggeredBy) {
  const attendees = [ev.assigned_to, ...(ev.attendees||[])].filter(Boolean);
  const uniqueAttendees = [...new Set(attendees)];
  for (const name of uniqueAttendees) {
    fetch(SLACK_PROXY, {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({
        ownerName: name,
        triggeredBy,
        isManual: false,
        contact: { first_name: "", last_name: "", company: "", phone: "", email: "", type: "" },
        calendarEvent: {
          title:    ev.title,
          date:     ev.date,
          time:     ev.time,
          type:     ev.type,
          notes:    ev.notes || "",
          location: ev.location || "",
        }
      })
    }).catch(()=>{});
  }
}

async function sendSlackDM({ ownerName, contact, triggeredBy, isManual = false }) {
  try {
    const res = await fetch(SLACK_PROXY, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ownerName, contact, triggeredBy, isManual }),
    });
    return res.ok;
  } catch (e) { console.error("Slack DM error:", e); return false; }
}

// ─── Responsive hook ──────────────────────────────────────────────────────────
function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return mobile;
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const Ic = ({ n, s = 16 }) => {
  const paths = {
    users:  "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
    tasks:  "M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11",
    note:   "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8",
    plus:   "M12 5v14M5 12h14",
    x:      "M18 6 6 18M6 6l12 12",
    edit:   "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z",
    trash:  "M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6",
    search: "M21 21l-4.35-4.35M17 11A6 6 0 1 0 5 11a6 6 0 0 0 12 0z",
    check:  "M20 6 9 17 4 12",
    external: "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3",
    upload: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12",
    cloud:  "M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z",
    phone:  "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z",
    mail:   "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6",
    person: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
    back:   "M19 12H5M12 19l-7-7 7-7",
    filter: "M22 3H2l8 9.46V19l4 2v-8.54L22 3",
    send:   "M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z",
    trophy: "M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22M18 2H6v7a6 6 0 0 0 12 0V2z",
    xcirc:  "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zM15 9l-6 6M9 9l6 6",
    undo:   "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8M3 3v5h5",
    logout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9",
    shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
    chart:  "M18 20V10M12 20V4M6 20v-6",
    cal:    "M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z",
  };
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={paths[n]} />
    </svg>
  );
};

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg:        "#eef1f5",
  surface:   "#ffffff",
  sidebar:   "#1a2535",
  border:    "#d1d9e0",
  border2:   "#b8c4cf",
  text:      "#0f172a",
  textSub:   "#334155",
  textMuted: "#64748b",
  teal:      "#2a8fa0",
  tealDim:   "#2a8fa018",
  tealBdr:   "#2a8fa040",
  red:       "#dc2626",
  amber:     "#d97706",
  green:     "#16a34a",
};

// Fonts: "Semibold" headings = system semibold stack, DIN Alternate for body/secondary
const F = {
  head: "'Barlow Semi Condensed', 'Arial Narrow', sans-serif",
  body: "'DIN Alternate', 'DINAlternate-Bold', 'Arial Narrow', 'Helvetica Neue', sans-serif",
};

const PRIORITY_COLOR = { High: C.red, Medium: C.amber, Low: C.teal };
const TYPE_COLOR     = { "Hot Lead": C.red, "Warm Lead": C.amber, "Cold Lead": "#7ecdd5" };
const STATUS_COLOR   = { Active: C.teal, Prospect: "#7ecdd5", Inactive: "#4b5563" };
const AVATAR_COLORS  = ["#3ea4b1","#2d8a96","#7ecdd5","#1a6b75","#4fb8c3","#56c5d0","#38909c"];

const uid = () => {
  // Generate a proper UUID v4 for Supabase compatibility
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
};
const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtTs    = iso => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const daysSince = iso => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};

// Returns { label, color, bg, border } for staleness badge
const stalenessInfo = (days) => {
  if (days === null) return null;
  if (days === 0) return { label: "Today",        color: C.teal,  bg: C.tealDim,      border: C.tealBdr };
  if (days === 1) return { label: "Yesterday",    color: C.teal,  bg: C.tealDim,      border: C.tealBdr };
  if (days <= 3)  return { label: `${days}d ago`, color: C.teal,  bg: C.tealDim,      border: C.tealBdr };
  if (days <= 7)  return { label: `${days}d ago`, color: C.amber, bg: C.amber + "18", border: C.amber + "44" };
  return               { label: `${days}d ago`, color: C.red,   bg: C.red   + "18", border: C.red   + "44" };
};

// ─── Atoms ────────────────────────────────────────────────────────────────────
const Avatar = ({ name = "?", size = 28 }) => {
  const color = AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.38, fontWeight: 700, color: "#fff", flexShrink: 0, fontFamily: F.head }}>
      {name[0]}
    </div>
  );
};

const Pill = ({ label, color }) => (
  <span style={{ background: color + "20", color, border: `1px solid ${color}44`, padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", fontFamily: F.body }}>{label}</span>
);

const Tag = ({ label }) => (
  <span style={{ background: "#1e3535", color: C.teal, padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, fontFamily: F.body }}>{label}</span>
);

const inp  = { width: "100%", background: "#ffffff", border: `1px solid ${C.border2}`, borderRadius: 9, color: C.text, padding: "10px 12px", fontSize: 14, boxSizing: "border-box", fontFamily: F.body, outline: "none" };
const btnP = { width: "100%", background: C.teal, border: "none", borderRadius: 10, color: "#fff", padding: "13px 0", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: F.head, letterSpacing: .8, marginTop: 10 };

const Fld = ({ label, children, span2 }) => (
  <div style={{ marginBottom: 14, gridColumn: span2 ? "span 2" : undefined }}>
    <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: C.textSub, letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 6, fontFamily: F.body }}>{label}</label>
    {children}
  </div>
);

// ─── Modal ────────────────────────────────────────────────────────────────────
const Modal = ({ title, onClose, children, isMobile }) => (
  <div style={{ position: "fixed", inset: 0, background: "#000000cc", zIndex: 2000, display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", padding: isMobile ? 0 : 16 }}
    onClick={e => e.target === e.currentTarget && onClose()}>
    <div style={{ background: C.surface, border: `1px solid ${C.border2}`, width: "100%", maxWidth: isMobile ? "100%" : 520, maxHeight: isMobile ? "92vh" : "90vh", overflow: "auto", borderRadius: isMobile ? "20px 20px 0 0" : 18, boxShadow: "0 -8px 40px #00000015" }}>
      {isMobile && <div style={{ width: 40, height: 4, background: C.border2, borderRadius: 2, margin: "12px auto 0" }} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: isMobile ? "16px 20px 0" : "22px 24px 0" }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.text, fontFamily: F.head, letterSpacing: .3 }}>{title}</h2>
        <button onClick={onClose} style={{ background: C.bg, border: "none", color: C.textSub, cursor: "pointer", borderRadius: 8, padding: 8, display: "flex" }}><Ic n="x" s={16} /></button>
      </div>
      <div style={{ padding: isMobile ? "16px 20px 32px" : "20px 24px 24px" }}>{children}</div>
    </div>
  </div>
);

// ─── Confirm ──────────────────────────────────────────────────────────────────
const Confirm = ({ message, onConfirm, onCancel, confirmLabel = "Confirm", confirmColor = C.red }) => (
  <div style={{ position: "fixed", inset: 0, background: "#000000cc", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
    onClick={e => e.target === e.currentTarget && onCancel()}>
    <div style={{ background: C.surface, border: `1px solid ${C.border2}`, borderRadius: 16, padding: 28, maxWidth: 360, width: "100%", textAlign: "center" }}>
      <div style={{ fontSize: 15, color: C.text, marginBottom: 22, lineHeight: 1.5, fontFamily: F.body }}>{message}</div>
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onCancel} style={{ flex: 1, background: "#e2e8f0", border: `1px solid ${C.border2}`, borderRadius: 9, color: C.textSub, padding: "10px 0", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: F.body }}>Cancel</button>
        <button onClick={onConfirm} style={{ flex: 1, background: confirmColor, border: "none", borderRadius: 9, color: "#fff", padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: F.head }}>{confirmLabel}</button>
      </div>
    </div>
  </div>
);

// ─── Sheet Toast ──────────────────────────────────────────────────────────────
function SheetToast({ status }) {
  if (!status) return null;
  const cfg = {
    sending: { bg: "#f1f5f9",         border: C.border2,         color: C.textSub, icon: "cloud", text: "Sending to Google Sheets…" },
    sent:    { bg: C.green + "18",    border: C.green + "44",    color: C.green,   icon: "check", text: "Saved to Google Sheets ✓"   },
    error:   { bg: C.red   + "18",    border: C.red   + "44",    color: C.red,     icon: "x",     text: "Couldn't reach Google Sheets" },
  }[status];
  return (
    <div style={{ position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)", zIndex: 9999, background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 12, padding: "12px 20px", display: "flex", alignItems: "center", gap: 10, boxShadow: "0 8px 32px #0008", whiteSpace: "nowrap" }}>
      <span style={{ color: cfg.color, display: "flex" }}><Ic n={cfg.icon} s={15} /></span>
      <span style={{ fontSize: 13, fontWeight: 600, color: cfg.color, fontFamily: F.body }}>{cfg.text}</span>
    </div>
  );
}

// ─── Pipeline Step Row ────────────────────────────────────────────────────────
function PipelineStepRow({ task, done, onToggle, locked }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: C.surface, border: `1px solid ${done ? C.teal + "33" : C.border}`, borderRadius: 10, marginBottom: 8, opacity: locked ? .4 : 1 }}>
      <div style={{ width: 26, height: 26, borderRadius: "50%", background: done ? C.teal : "#f1f5f9", border: `1.5px solid ${done ? C.teal : "#444"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: locked ? "default" : "pointer" }}
        onClick={locked ? undefined : onToggle}>
        {done ? <Ic n="check" s={13} /> : <span style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, fontFamily: F.head }}>{task.step}</span>}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: done ? C.textMuted : C.text, fontWeight: 600, textDecoration: done ? "line-through" : "none", fontFamily: F.body }}>{task.title}</div>
      </div>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: PRIORITY_COLOR[task.priority], flexShrink: 0 }} title={task.priority} />
    </div>
  );
}

// ─── Outcome Banner ───────────────────────────────────────────────────────────
function OutcomeBanner({ outcome, onUndo }) {
  const sold = outcome === "sold";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, background: sold ? C.green + "18" : C.red + "18", border: `1px solid ${sold ? C.green + "44" : C.red + "44"}`, borderRadius: 10, padding: "11px 14px", marginBottom: 14 }}>
      <Ic n={sold ? "trophy" : "xcirc"} s={18} />
      <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: sold ? C.green : C.red, fontFamily: F.head, letterSpacing: .3 }}>
        {sold ? "Job Sold — Congratulations! 🎉" : "Job Lost"}
      </div>
      <button onClick={onUndo} style={{ background: "none", border: `1px solid ${C.border2}`, borderRadius: 7, color: C.textSub, cursor: "pointer", padding: "5px 10px", fontSize: 11, display: "flex", alignItems: "center", gap: 5, fontFamily: F.body }}>
        <Ic n="undo" s={12} /> Undo
      </button>
    </div>
  );
}

// ─── Note Composer ────────────────────────────────────────────────────────────
function NoteComposer({ onAdd, currentUser }) {
  const [text, setText] = useState("");
  const submit = () => {
    if (!text.trim()) return;
    onAdd({ id: uid(), text: text.trim(), author: currentUser.name, ts: new Date().toISOString() });
    setText("");
  };
  return (
    <div style={{ background: "#ffffff", border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, marginBottom: 14 }}>
      <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Add a note…"
        onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
        style={{ ...inp, height: 68, resize: "none", background: "transparent", border: "none", padding: 0, fontSize: 13, marginBottom: 10 }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <Avatar name={currentUser.name} size={22} />
          <span style={{ fontSize: 12, color: C.textSub, fontFamily: F.body }}>{currentUser.name}</span>
        </div>
        <button onClick={submit} style={{ display: "flex", alignItems: "center", gap: 7, background: C.teal, border: "none", borderRadius: 8, color: "#fff", padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: F.head, flexShrink: 0 }}>
          <Ic n="send" s={13} /> Add Note
        </button>
      </div>
    </div>
  );
}

function NoteEntry({ note, onDelete }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "11px 13px", marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <Avatar name={note.author} size={20} />
          <span style={{ fontSize: 12, fontWeight: 600, color: C.textSub, fontFamily: F.body }}>{note.author}</span>
          <span style={{ fontSize: 10, color: C.textMuted }}>·</span>
          <span style={{ fontSize: 10, color: C.textMuted, fontFamily: F.body }}>{fmtTs(note.ts)}</span>
        </div>
        {onDelete && <button onClick={onDelete} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", padding: 2, display: "flex" }}><Ic n="trash" s={12} /></button>}
      </div>
      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, whiteSpace: "pre-wrap", fontFamily: F.body }}>{note.text}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LOGIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
function LoginPage({ onLogin, usersLoaded }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  const attempt = () => {
    if (!usersLoaded) { setError("Still loading — please wait a moment and try again."); return; }
    setLoading(true);
    setError("");
    // Aggressively clean username — iOS Safari can add spaces, capitals, autocorrect
    const cleanUser = username.trim().toLowerCase().replace(/\s+/g, "");
    const cleanPass = password.trim();
    setTimeout(() => {
      const ok = onLogin(cleanUser, cleanPass);
      if (!ok) { setError("Incorrect username or password."); setLoading(false); }
    }, 400);
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 380 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <img src={logo} alt="JG Graphics" style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", marginBottom: 14 }} />
          <div style={{ fontSize: 36, fontWeight: 700, color: C.text, fontFamily: F.head, letterSpacing: 1, marginBottom: 6 }}>
            JG <span style={{ color: C.teal }}>Graphics</span>
          </div>
          <div style={{ fontSize: 12, color: C.textMuted, letterSpacing: 2.5, textTransform: "uppercase", fontFamily: F.body }}>JG Graphics CRM</div>
        </div>

        {/* Card */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: "32px 28px", boxShadow: "0 24px 60px #00000018" }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.text, fontFamily: F.head, marginBottom: 6, letterSpacing: .3 }}>Sign in</div>
          <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 28, fontFamily: F.body }}>Enter your credentials to access your dashboard.</div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: C.textSub, letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 6, fontFamily: F.body }}>Username</label>
            <input
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={e => e.key === "Enter" && attempt()}
              placeholder="e.g. alex"
              style={{ ...inp, fontSize: 15 }}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              autoFocus={false}
              spellCheck={false}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: C.textSub, letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 6, fontFamily: F.body }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && attempt()}
              placeholder="••••••••"
              style={{ ...inp, fontSize: 15 }}
              autoComplete="current-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>

          {error && (
            <div style={{ background: C.red + "18", border: `1px solid ${C.red}44`, borderRadius: 8, padding: "10px 12px", marginBottom: 16, fontSize: 13, color: C.red, fontFamily: F.body }}>
              {error}
            </div>
          )}

          <button onClick={attempt} disabled={loading || !usersLoaded} style={{ ...btnP, opacity: (loading || !usersLoaded) ? .7 : 1, fontSize: 16, padding: "14px 0" }}>
            {!usersLoaded ? "Loading…" : loading ? "Signing in…" : "Sign In"}
          </button>
        </div>

      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Calendar Components ───────────────────────────────────────────────────────

const EVENT_TYPES = ["Call", "Meeting", "Follow-up", "Demo", "Other"];
const EVENT_COLORS = { "Call": "#3ea4b1", "Meeting": "#a78bfa", "Follow-up": "#f59e0b", "Demo": "#10b981", "Other": "#64748b" };

function CalendarGrid({ events, onSelectDay, selectedDay, month, year }) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date().toISOString().slice(0, 10);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 4 }}>
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: C.textMuted, padding: "4px 0", fontFamily: F.body, letterSpacing: 1 }}>{d}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
          const dayEvents = events.filter(e => e.date === dateStr);
          const isToday = dateStr === today;
          const isSelected = dateStr === selectedDay;
          return (
            <div key={i} onClick={() => onSelectDay(dateStr)}
              style={{ minHeight: 52, padding: "4px 5px", background: isSelected ? C.tealDim : isToday ? "#e0f2f7" : C.surface, border: `1px solid ${isSelected ? C.teal : isToday ? C.teal+"44" : C.border}`, borderRadius: 8, cursor: "pointer" }}>
              <div style={{ fontSize: 11, fontWeight: isToday ? 700 : 400, color: isToday ? C.teal : C.textSub, marginBottom: 3, fontFamily: F.body }}>{d}</div>
              {dayEvents.slice(0,3).map(e => (
                <div key={e.id} style={{ fontSize: 9, background: EVENT_COLORS[e.type]+"33", color: EVENT_COLORS[e.type], borderRadius: 3, padding: "1px 4px", marginBottom: 2, fontFamily: F.body, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {e.time} {e.title}
                </div>
              ))}
              {dayEvents.length > 3 && <div style={{ fontSize: 9, color: C.textMuted, fontFamily: F.body }}>+{dayEvents.length - 3} more</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventForm({ contacts, users, onSave, onClose, init, isAdmin, currentUserName }) {
  const refs = {
    title: useRef(), type: useRef(), date: useRef(), time: useRef(),
    assigned_to: useRef(), contact_id: useRef(), notes: useRef(), location: useRef(),
  };
  const today = new Date().toISOString().slice(0, 10);
  const now = new Date(); now.setMinutes(Math.ceil(now.getMinutes()/15)*15);
  const defTime = now.toTimeString().slice(0,5);
  const allReps = (users||[]).filter(u => u.role === "rep");
  const initAttendees = init?.attendees || [];
  const [attendees, setAttendees] = useState(initAttendees);

  const toggleAttendee = name => {
    setAttendees(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Fld label="Title">
        <input style={inp} ref={refs.title} defaultValue={init?.title || ""} placeholder="e.g. Discovery Call with Acme" />
      </Fld>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Fld label="Type">
          <select style={inp} ref={refs.type} defaultValue={init?.type || "Call"}>
            {EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </Fld>
        <Fld label="Assign To">
          {isAdmin ? (
            <select style={inp} ref={refs.assigned_to} defaultValue={init?.assigned_to || ""}>
              {allReps.map(u => <option key={u.name} value={u.name}>{u.name}</option>)}
            </select>
          ) : (
            <input style={{ ...inp, background: "#f1f5f9", color: C.textMuted }} value={currentUserName} readOnly />
          )}
        </Fld>
        <Fld label="Date">
          <input style={inp} type="date" ref={refs.date} defaultValue={init?.date || today} />
        </Fld>
        <Fld label="Time">
          <input style={inp} type="time" ref={refs.time} defaultValue={init?.time || defTime} />
        </Fld>
      </div>
      {allReps.filter(u => u.name !== currentUserName).length > 0 && (
        <Fld label="Invite Others (optional)">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
            {allReps.filter(u => u.name !== currentUserName).map(u => {
              const sel = attendees.includes(u.name);
              return (
                <button key={u.name} type="button" onClick={() => toggleAttendee(u.name)}
                  style={{ display: "flex", alignItems: "center", gap: 6, background: sel ? C.tealDim : "#ffffff", border: `1px solid ${sel ? C.teal : C.border2}`, borderRadius: 20, color: sel ? C.teal : C.textMuted, padding: "5px 12px", cursor: "pointer", fontSize: 12, fontFamily: F.body, fontWeight: sel ? 700 : 400 }}>
                  {sel && <Ic n="check" s={11} />} {u.name.split(" ")[0]}
                </button>
              );
            })}
          </div>
        </Fld>
      )}
      <Fld label="Linked Contact (optional)">
        <select style={inp} ref={refs.contact_id} defaultValue={init?.contact_id || ""}>
          <option value="">— None —</option>
          {(contacts||[]).map(c => <option key={c.id} value={c.id}>{`${c.first_name||""} ${c.last_name||""}`.trim()||c.name} {c.company ? `· ${c.company}` : ""}</option>)}
        </select>
      </Fld>
      <Fld label="Location (optional)">
        <input style={inp} ref={refs.location} defaultValue={init?.location || ""} placeholder="e.g. JG Graphics, 123 Main St…" />
      </Fld>
      <Fld label="Notes">
        <textarea style={{ ...inp, minHeight: 70, resize: "vertical" }} ref={refs.notes} defaultValue={init?.notes || ""} placeholder="Any details…" />
      </Fld>
      <div style={{ display: "flex", gap: 10 }}>
        <button style={btnP} onClick={() => {
          const get = k => refs[k].current?.value || "";
          const title = get("title").trim();
          if (!title) return;
          onSave({
            title, type: get("type") || "Call", date: get("date") || today,
            time: get("time") || defTime,
            assigned_to: isAdmin ? get("assigned_to") : currentUserName,
            contact_id: get("contact_id") || null,
            location: get("location") || "",
            notes: get("notes"),
            attendees,
          });
          onClose();
        }}>Save Event</button>
        <button onClick={onClose} style={{ flex: 1, background: "none", border: `1px solid ${C.border2}`, borderRadius: 9, color: C.textMuted, padding: "10px 0", cursor: "pointer", fontSize: 13, fontFamily: F.body }}>Cancel</button>
      </div>
    </div>
  );
}

function EventCard({ event, contacts, onDelete, onEdit, canEdit }) {
  const contact = contacts?.find(c => c.id === event.contact_id);
  const col = EVENT_COLORS[event.type] || C.teal;
  return (
    <div style={{ background: C.surface, border: `1px solid ${col}33`, borderLeft: `3px solid ${col}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: F.head, marginBottom: 3 }}>{event.title}</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, background: col+"22", color: col, borderRadius: 4, padding: "2px 7px", fontFamily: F.body, fontWeight: 700 }}>{event.type}</span>
            <span style={{ fontSize: 11, color: C.textMuted, fontFamily: F.body }}>{event.date} at {event.time}</span>
            {event.assigned_to && <span style={{ fontSize: 11, color: C.textSub, fontFamily: F.body }}>→ {event.assigned_to}</span>}
          </div>
          {contact && <div style={{ fontSize: 11, color: C.teal, marginTop: 4, fontFamily: F.body }}>📎 {`${contact.first_name||""} ${contact.last_name||""}`.trim()||contact.name} · {contact.company}</div>}
          {event.location && <div style={{ fontSize: 11, color: C.textSub, marginTop: 4, fontFamily: F.body }}>📍 {event.location}</div>}
          {(() => {
            const all = [...new Set([event.assigned_to, ...(event.attendees||[])].filter(Boolean))];
            if (all.length === 0) return null;
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, color: C.textMuted, fontFamily: F.body }}>👥</span>
                {all.map(name => (
                  <span key={name} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: C.tealDim, border: `1px solid ${C.tealBdr}`, borderRadius: 20, padding: "2px 9px", fontSize: 11, color: C.teal, fontFamily: F.body, fontWeight: 600 }}>
                    {name.split(" ")[0]}
                    {name === event.assigned_to && <span style={{ fontSize: 9, opacity: .7 }}>★</span>}
                  </span>
                ))}
              </div>
            );
          })()}
          {event.notes && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6, fontFamily: F.body }}>{event.notes}</div>}
        </div>
        {canEdit && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0, marginLeft: 10 }}>
            {onEdit && <button onClick={onEdit} style={{ background: C.tealDim, border: `1px solid ${C.tealBdr}`, borderRadius: 6, color: C.teal, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontFamily: F.body }}>Edit</button>}
            <button onClick={onDelete} style={{ background: C.red+"18", border: `1px solid ${C.red}44`, borderRadius: 6, color: C.red, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontFamily: F.body }}>Delete</button>
          </div>
        )}
      </div>
    </div>
  );
}

function CalendarView({ events, contacts, users, onAdd, onDelete, onEdit, isAdmin, currentUserName, isMobile }) {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear]   = useState(today.getFullYear());
  const [selectedDay, setSelectedDay] = useState(today.toISOString().slice(0, 10));
  const [view, setView]   = useState("month"); // "month" | "upcoming"
  const [addModal, setAddModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);

  const myEvents = isAdmin ? events : events.filter(e => e.assigned_to === currentUserName || (e.attendees||[]).includes(currentUserName));
  const monthEvents = myEvents.filter(e => {
    const d = new Date(e.date);
    return d.getMonth() === month && d.getFullYear() === year;
  });
  const dayEvents = myEvents.filter(e => e.date === selectedDay).sort((a,b) => a.time.localeCompare(b.time));
  const upcomingSorted = myEvents.filter(e => e.date >= today.toISOString().slice(0,10)).sort((a,b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));

  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y-1); } else setMonth(m => m-1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y+1); } else setMonth(m => m+1); };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ fontFamily: F.head, fontWeight: 700, fontSize: isMobile ? 22 : 26, color: C.text, margin: 0, letterSpacing: .3 }}>Calendar</h1>
          <p style={{ color: C.textMuted, fontSize: 12, marginTop: 4, fontFamily: F.body }}>{myEvents.length} events scheduled</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ display: "flex", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
            {[["month","Month"],["upcoming","Upcoming"]].map(([v,l]) => (
              <button key={v} onClick={() => setView(v)} style={{ padding: "7px 14px", background: view===v ? C.tealDim : "transparent", border: "none", borderRight: `1px solid ${C.border}`, color: view===v ? C.teal : C.textMuted, cursor: "pointer", fontSize: 12, fontWeight: view===v ? 700 : 400, fontFamily: F.body }}>{l}</button>
            ))}
          </div>
          <button onClick={() => setAddModal(true)} style={{ display: "flex", alignItems: "center", gap: 6, background: C.teal, border: "none", borderRadius: 8, color: "#fff", padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: F.head }}>
            + Add Event
          </button>
        </div>
      </div>

      {view === "month" && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 300px", gap: 18 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <button onClick={prevMonth} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7, color: C.textSub, padding: "6px 12px", cursor: "pointer", fontFamily: F.body }}>‹</button>
              <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 16, color: C.text }}>{MONTHS[month]} {year}</div>
              <button onClick={nextMonth} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7, color: C.textSub, padding: "6px 12px", cursor: "pointer", fontFamily: F.body }}>›</button>
            </div>
            <CalendarGrid events={monthEvents} onSelectDay={setSelectedDay} selectedDay={selectedDay} month={month} year={year} />
          </div>
          <div>
            <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 12 }}>
              {new Date(selectedDay+"T12:00:00").toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric" })}
            </div>
            {dayEvents.length === 0 && <div style={{ color: C.textMuted, fontSize: 13, fontFamily: F.body }}>No events this day.</div>}
            {dayEvents.map(e => (
              <EventCard key={e.id} event={e} contacts={contacts} canEdit={isAdmin || e.assigned_to === currentUserName || e.created_by === currentUserName}
                onDelete={() => onDelete(e.id)}
                onEdit={() => setEditEvent(e)} />
            ))}
            <button onClick={() => { setAddModal(true); }} style={{ width: "100%", background: "none", border: `1px dashed ${C.border2}`, borderRadius: 9, color: C.textMuted, padding: "10px 0", cursor: "pointer", fontSize: 12, fontFamily: F.body, marginTop: 8 }}>
              + Add event on this day
            </button>
          </div>
        </div>
      )}

      {view === "upcoming" && (
        <div>
          {upcomingSorted.length === 0 && <div style={{ color: C.textMuted, fontSize: 13, fontFamily: F.body }}>No upcoming events.</div>}
          {upcomingSorted.map(e => (
            <EventCard key={e.id} event={e} contacts={contacts} canEdit={isAdmin || e.assigned_to === currentUserName || e.created_by === currentUserName}
              onDelete={() => onDelete(e.id)}
              onEdit={() => setEditEvent(e)} />
          ))}
        </div>
      )}

      {(addModal || editEvent) && (
        <div style={{ position: "fixed", inset: 0, background: "#000a", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={() => { setAddModal(false); setEditEvent(null); }}>
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 18, color: C.text, marginBottom: 18 }}>{editEvent ? "Edit Event" : "New Event"}</div>
            <EventForm
              contacts={contacts} users={users} isAdmin={isAdmin} currentUserName={currentUserName}
              init={editEvent ? editEvent : { date: selectedDay }}
              onSave={f => {
                if (editEvent) { onEdit(editEvent.id, f); setEditEvent(null); }
                else onAdd({ id: uid(), ...f, created_by: isAdmin ? "Admin" : currentUserName, created: new Date().toISOString() });
              }}
              onClose={() => { setAddModal(false); setEditEvent(null); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function AdminDashboard({ contacts, tasksDone, notes, users, events, onLogout, isMobile, db, currentUser }) {
  const [adminTab, setAdminTab]   = useState("clients");
  const [activeRep, setActiveRep] = useState("all");
  const [search, setSearch]       = useState("");
  const [detail, setDetail]       = useState(null);
  const [detailTab, setDetailTab] = useState("tasks");
  const [userModal, setUserModal]             = useState(null);
  const [contactModal, setContactModal]       = useState(false);
  const [newContactErr, setNewContactErr] = useState("");
  const [editContactModal, setEditContactModal] = useState(null);
  const [confirm, setConfirm]                 = useState(null);
  const [slackStatus, setSlackStatus]         = useState(null); // { id, status: "sending"|"sent"|"error" }
  const [emailModal, setEmailModal]           = useState(null);

  const notifyRep = async (contact, isManual = false) => {
    try {
      if (!isManual) {
        // For auto-notify on new contact, fire and forget without touching UI state
        sendSlackDM({ ownerName: contact.owner, contact, triggeredBy: "Admin", isManual: false })
          .catch(e => console.error("Slack notify error:", e));
        return;
      }
      setSlackStatus({ id: contact.id, status: "sending" });
      const ok = await sendSlackDM({
        ownerName:   contact.owner,
        contact,
        triggeredBy: "Admin",
        isManual,
      });
      setSlackStatus({ id: contact.id, status: ok ? "sent" : "error" });
      setTimeout(() => setSlackStatus(null), 3000);
    } catch(e) {
      console.error("notifyRep error:", e);
    }
  };

  const setOutcome = async (id, outcome) => {
    const outcome_at = outcome ? new Date().toISOString() : null;
    await db.updateContact(id, { outcome, outcome_at });
    if (outcome === "sold" || outcome === "lost") {
      db.addNote(id, { id: uid(), text: outcome === "sold" ? "✅ Marked as Sold" : "❌ Marked as Lost", author: "Admin", ts: new Date().toISOString() });
    } else if (outcome === null) {
      db.addNote(id, { id: uid(), text: "↩️ Outcome undone — returned to active", author: "Admin", ts: new Date().toISOString() });
    }
  };

  const addNote    = (cid, note) => db.addNote(cid, note);
  const deleteNote = (cid, nid) => db.deleteNote(cid, nid);

  // Ticker so the UI reacts when the 5-minute grace period expires
  const nowRef = useRef(Date.now());
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const id = setInterval(() => { nowRef.current = Date.now(); forceUpdate(n => n + 1); }, 60_000);
    return () => clearInterval(id);
  }, []);

  const GRACE = 5 * 60 * 1000;
  const isArchived = c => c.outcome && (!c.outcome_at || (nowRef.current - new Date(c.outcome_at).getTime()) >= GRACE);

  const reps = users.filter(u => u.role === "rep");
  const repsWithCounts = reps.map(rep => ({
    name: rep.name,
    count: contacts.filter(c => c.owner === rep.name && !isArchived(c)).length,
    sold:  contacts.filter(c => c.owner === rep.name && c.outcome === "sold").length,
    lost:  contacts.filter(c => c.owner === rep.name && c.outcome === "lost").length,
  }));

  const q = search.toLowerCase();
  const visibleContacts = contacts.filter(c =>
    !isArchived(c) &&
    (activeRep === "all" || c.owner === activeRep) &&
    (!q || `${c.first_name||''} ${c.last_name||''}`.toLowerCase().includes(q) || (c.name||'').toLowerCase().includes(q) || c.company?.toLowerCase().includes(q))
  );

  const totalSold = contacts.filter(c => c.outcome === "sold").length;
  const totalLost = contacts.filter(c => c.outcome === "lost").length;

  const addUser    = u => db.addUser(u);
  const updateUser = (id, patch) => db.updateUser(id, patch);
  const deleteUser = id => db.deleteUser(id);

  const newContactRefs = {
    first_name: useRef(), last_name: useRef(), company: useRef(),
    email: useRef(), phone: useRef(), address: useRef(),
    type: useRef(), status: useRef(), owner: useRef(), tags: useRef(),
  };

  const checkDuplicateRefs = () => {
    const email   = newContactRefs.email.current?.value?.trim() || "";
    const phone   = newContactRefs.phone.current?.value?.trim() || "";
    const company = newContactRefs.company.current?.value?.trim() || "";
    if (email) {
      const m = contacts.find(c => c.email?.toLowerCase().trim() === email.toLowerCase());
      if (m) { setNewContactErr(`${`${m.first_name||""} ${m.last_name||""}`.trim()||m.name||"Someone"} with this email already exists — owned by ${m.owner||"another rep"}.`); return true; }
    }
    if (phone) {
      const m = contacts.find(c => c.phone?.replace(/\D/g,"") === phone.replace(/\D/g,"") && c.phone.trim());
      if (m) { setNewContactErr(`${`${m.first_name||""} ${m.last_name||""}`.trim()||m.name||"Someone"} with this phone already exists — owned by ${m.owner||"another rep"}.`); return true; }
    }
    if (company) {
      const m = contacts.find(c => c.company?.toLowerCase().trim() === company.toLowerCase() && c.company.trim());
      if (m) { setNewContactErr(`${`${m.first_name||""} ${m.last_name||""}`.trim()||m.name||"Someone"} from ${company} already exists — owned by ${m.owner||"another rep"}.`); return true; }
    }
    return false;
  };

  const AdminContactForm = ({ onSave }) => {
    const cols = isMobile ? "1fr" : "1fr 1fr";
    const clr = () => setNewContactErr("");
    return (
      <div style={{ display: "grid", gridTemplateColumns: cols, gap: "0 14px" }}>
        <Fld label="First Name"><input style={inp} ref={newContactRefs.first_name} defaultValue="" placeholder="Jane" /></Fld>
        <Fld label="Last Name"><input style={inp} ref={newContactRefs.last_name} defaultValue="" placeholder="Smith" /></Fld>
        <Fld label="Company"><input style={inp} ref={newContactRefs.company} defaultValue="" placeholder="Acme Inc." onChange={clr} /></Fld>
        <Fld label="Email"><input style={inp} ref={newContactRefs.email} defaultValue="" placeholder="jane@co.com" onChange={clr} /></Fld>
        <Fld label="Phone"><input style={inp} ref={newContactRefs.phone} defaultValue="" placeholder="555-0100" onChange={clr} /></Fld>
        <Fld label="Address"><input style={inp} ref={newContactRefs.address} defaultValue="" placeholder="123 Main St, City, ST" /></Fld>
        <Fld label="Type">
          <select style={inp} ref={newContactRefs.type} defaultValue="Hot Lead">{["Hot Lead","Warm Lead","Cold Lead"].map(o => <option key={o}>{o}</option>)}</select>
        </Fld>
        <Fld label="Status">
          <select style={inp} ref={newContactRefs.status} defaultValue="Active">{["Active","Prospect","Inactive"].map(o => <option key={o}>{o}</option>)}</select>
        </Fld>
        <Fld label="Assign to Rep" span2>
          <select style={inp} ref={newContactRefs.owner} defaultValue={reps[0]?.name || ""}>
            {reps.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
          </select>
        </Fld>
        <Fld label="Tags (comma-separated)" span2><input style={inp} ref={newContactRefs.tags} defaultValue="" placeholder="Logo, Print…" /></Fld>
        {newContactErr && (
          <div style={{ gridColumn: "span 2", background: C.red + "18", border: `1px solid ${C.red}44`, borderRadius: 8, padding: "10px 12px", fontSize: 13, color: C.red, fontFamily: F.body }}>
            ⛔ {newContactErr}
          </div>
        )}
        <div style={{ gridColumn: "span 2" }}>
          <button style={btnP} onClick={() => {
            const get = k => newContactRefs[k].current?.value || "";
            const first_name = get("first_name").trim();
            if (!first_name) { setNewContactErr("First name is required."); return; }
            if (checkDuplicateRefs()) return;
            const f = {
              first_name, last_name: get("last_name"), company: get("company"),
              email: get("email"), phone: get("phone"), address: get("address"),
              type: get("type") || "Hot Lead", status: get("status") || "Active",
              owner: get("owner") || reps[0]?.name || "",
              tags: get("tags").split(",").map(t => t.trim()).filter(Boolean),
            };
            onSave({ ...f, name: `${f.first_name} ${f.last_name}`.trim() });
            Object.values(newContactRefs).forEach(r => { if (r.current) r.current.value = ""; });
            if (newContactRefs.type.current) newContactRefs.type.current.value = "Hot Lead";
            if (newContactRefs.status.current) newContactRefs.status.current.value = "Active";
            if (newContactRefs.owner.current) newContactRefs.owner.current.value = reps[0]?.name || "";
            setNewContactErr("");
            setContactModal(false);
          }}>Add Contact</button>
        </div>
      </div>
    );
  };

  const AdminEditContactForm = ({ init = {}, onSave }) => {
    const [f, setF] = useState({
      first_name: "", last_name: "", company: "", email: "", phone: "", address: "",
      type: "Hot Lead", status: "Active", owner: reps[0]?.name || "",
      ...init,
      tags: Array.isArray(init.tags) ? init.tags.join(", ") : (init.tags || ""),
    });
    const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));
    const cols = isMobile ? "1fr" : "1fr 1fr";
    return (
      <div style={{ display: "grid", gridTemplateColumns: cols, gap: "0 14px" }}>
        <Fld label="First Name"><input style={inp} value={f.first_name} onChange={set("first_name")} placeholder="Jane" /></Fld>
        <Fld label="Last Name"><input style={inp} value={f.last_name} onChange={set("last_name")} placeholder="Smith" /></Fld>
        <Fld label="Company"><input style={inp} value={f.company} onChange={set("company")} placeholder="Acme Inc." /></Fld>
        <Fld label="Email"><input style={inp} value={f.email} onChange={set("email")} placeholder="jane@co.com" /></Fld>
        <Fld label="Phone"><input style={inp} value={f.phone} onChange={set("phone")} placeholder="555-0100" /></Fld>
        <Fld label="Address"><input style={inp} value={f.address} onChange={set("address")} placeholder="123 Main St, City, ST" /></Fld>
        <Fld label="Type">
          <select style={inp} value={f.type} onChange={set("type")}>{["Hot Lead","Warm Lead","Cold Lead"].map(o => <option key={o}>{o}</option>)}</select>
        </Fld>
        <Fld label="Status">
          <select style={inp} value={f.status} onChange={set("status")}>{["Active","Prospect","Inactive"].map(o => <option key={o}>{o}</option>)}</select>
        </Fld>
        <Fld label="Assign to Rep" span2>
          <select style={inp} value={f.owner} onChange={set("owner")}>
            {reps.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
          </select>
        </Fld>
        <Fld label="Tags (comma-separated)" span2><input style={inp} value={f.tags} onChange={set("tags")} placeholder="Logo, Print…" /></Fld>
        <div style={{ gridColumn: "span 2" }}>
          <button style={btnP} onClick={() => {
            if (!f.first_name.trim()) return;
            onSave({ ...f, name: `${f.first_name} ${f.last_name}`.trim(), tags: f.tags.split(",").map(t => t.trim()).filter(Boolean) });
            setEditContactModal(null);
          }}>Save Changes</button>
        </div>
      </div>
    );
  };

  const UserForm = ({ init = {}, onSave, isEdit = false }) => {
    const [f, setF] = useState({ name: "", username: "", password: "", role: "rep", email: "", phone: "", ...init });
    const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));
    const [err, setErr] = useState("");
    const save = () => {
      if (!f.name.trim() || !f.username.trim() || !f.password.trim()) { setErr("Name, username and password are required."); return; }
      const taken = users.find(u => u.username === f.username.toLowerCase().trim() && u.id !== f.id);
      if (taken) { setErr("That username is already taken."); return; }
      onSave({ ...f, username: f.username.toLowerCase().trim() });
      setUserModal(null);
    };
    const cols = isMobile ? "1fr" : "1fr 1fr";
    return (
      <div style={{ display: "grid", gridTemplateColumns: cols, gap: "0 14px" }}>
        <Fld label="Full Name" span2><input style={inp} value={f.name} onChange={set("name")} placeholder="Jane Smith" /></Fld>
        <Fld label="Username"><input style={inp} value={f.username} onChange={set("username")} placeholder="janesmith" autoComplete="off" /></Fld>
        <Fld label="Password"><input style={inp} value={f.password} onChange={set("password")} placeholder="••••••••" autoComplete="new-password" /></Fld>
        <Fld label="Role">
          <select style={inp} value={f.role} onChange={set("role")}>
            <option value="rep">Sales Rep</option>
            <option value="admin">Admin</option>
          </select>
        </Fld>
        <Fld label="Email"><input style={inp} value={f.email} onChange={set("email")} placeholder="jane@jg.graphics" /></Fld>
        <Fld label="Phone"><input style={inp} value={f.phone} onChange={set("phone")} placeholder="555-0100" /></Fld>
        {err && <div style={{ gridColumn: "span 2", background: C.red + "18", border: `1px solid ${C.red}44`, borderRadius: 8, padding: "9px 12px", fontSize: 13, color: C.red, fontFamily: F.body }}>{err}</div>}
        <div style={{ gridColumn: "span 2" }}>
          <button style={btnP} onClick={save}>{isEdit ? "Save Changes" : "Create User"}</button>
        </div>
      </div>
    );
  };

  const renderUsersPage = () => (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
        <div>
          <h1 style={{ fontFamily: F.head, fontWeight: 700, fontSize: isMobile ? 22 : 26, color: C.text, margin: 0, letterSpacing: .3 }}>Team Members</h1>
          <p style={{ color: C.textMuted, fontSize: 12, marginTop: 4, fontFamily: F.body }}>{users.length} users · {reps.length} sales reps · {users.filter(u => u.role === "admin").length} admins</p>
        </div>
        <button onClick={() => setUserModal("new")} style={{ display: "flex", alignItems: "center", gap: 7, background: C.teal, border: "none", borderRadius: 9, color: "#fff", padding: "10px 18px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: F.head, letterSpacing: .5, flexShrink: 0 }}>
          <Ic n="plus" s={14} /> New User
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(300px,1fr))", gap: 14 }}>
        {users.map(u => {
          const repContacts = contacts.filter(c => c.owner === u.name);
          const sold = repContacts.filter(c => c.outcome === "sold").length;
          const lost = repContacts.filter(c => c.outcome === "lost").length;
          const open = repContacts.filter(c => !c.outcome).length;
          return (
            <div key={u.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
                <div style={{ position: "relative" }}>
                  <Avatar name={u.name} size={46} />
                  {u.role === "admin" && (
                    <div style={{ position: "absolute", bottom: -2, right: -2, background: C.amber, borderRadius: "50%", width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${C.surface}` }}>
                      <Ic n="shield" s={9} />
                    </div>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 16, color: C.text, letterSpacing: .2 }}>{u.name}</div>
                  <div style={{ fontSize: 12, color: C.textSub, fontFamily: F.body, marginTop: 1 }}>@{u.username}</div>
                  <div style={{ marginTop: 5 }}>
                    <Pill label={u.role === "admin" ? "Admin" : "Sales Rep"} color={u.role === "admin" ? C.amber : C.teal} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={() => setUserModal(u)} style={{ background: C.bg, border: `1px solid ${C.border2}`, color: C.textSub, cursor: "pointer", borderRadius: 7, padding: 7, display: "flex" }}><Ic n="edit" s={13} /></button>
                  <button onClick={() => setConfirm({ message: `Delete user "${u.name}"?`, confirmLabel: "Delete User", confirmColor: C.red, onConfirm: () => { deleteUser(u.id); setConfirm(null); } })}
                    style={{ background: C.bg, border: `1px solid ${C.border2}`, color: C.textSub, cursor: "pointer", borderRadius: 7, padding: 7, display: "flex" }}><Ic n="trash" s={13} /></button>
                </div>
              </div>
              {(u.email || u.phone) && (
                <div style={{ marginBottom: 14, display: "flex", flexDirection: "column", gap: 5 }}>
                  {u.email && <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: C.textSub, fontFamily: F.body }}><Ic n="mail" s={12} />{u.email}</div>}
                  {u.phone && <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: C.textSub, fontFamily: F.body }}><Ic n="phone" s={12} />{u.phone}</div>}
                </div>
              )}
              {u.role === "rep" && (
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 10, fontFamily: F.body }}>Pipeline Stats</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[["Total", repContacts.length, C.teal],["Open", open, C.textSub],["Sold", sold, C.green],["Lost", lost, C.red]].map(([lbl,val,col]) => (
                      <div key={lbl} style={{ flex: 1, background: C.bg, borderRadius: 8, padding: "8px 4px", textAlign: "center" }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: col, fontFamily: F.head }}>{val}</div>
                        <div style={{ fontSize: 9, color: C.textMuted, letterSpacing: .8, textTransform: "uppercase", fontFamily: F.body }}>{lbl}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {u.created && (
                <div style={{ marginTop: 12, fontSize: 10, color: C.textMuted, fontFamily: F.body }}>
                  Joined {new Date(u.created).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderContactDetail = () => {
    const c = contacts.find(x => x.id === detail.id) || detail;
    const cTasks = getTasksForContact(c.id);
    const cNotes = notes[c.id] || [];
    const doneCount = cTasks.filter(t => tasksDone[t.id]).length;
    const TabBtn = ({ id, icon, label, count }) => (
      <button onClick={() => setDetailTab(id)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0", background: detailTab === id ? C.tealDim : "transparent", border: "none", borderBottom: `2px solid ${detailTab === id ? C.teal : "transparent"}`, color: detailTab === id ? C.teal : C.textMuted, cursor: "pointer", fontSize: 13, fontWeight: detailTab === id ? 700 : 400, fontFamily: F.body }}>
        <Ic n={icon} s={14} />{label}
        {count > 0 && <span style={{ background: detailTab === id ? C.teal : "#cbd5e1", color: detailTab === id ? "#fff" : C.textMuted, fontSize: 10, fontWeight: 700, borderRadius: 10, padding: "1px 7px" }}>{count}</span>}
      </button>
    );
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <button onClick={() => setDetail(null)} style={{ background: "none", border: "none", color: C.teal, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 6, fontWeight: 600, padding: 0, fontFamily: F.body }}>
            <Ic n="back" s={14} /> Back
          </button>
          <div style={{ display: "flex", gap: 8 }}>

            <button onClick={() => setEditContactModal(c)} style={{ display: "flex", alignItems: "center", gap: 6, background: C.tealDim, border: `1px solid ${C.tealBdr}`, borderRadius: 8, color: C.teal, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: F.body }}>
              <Ic n="edit" s={13} /> Edit
            </button>
            <button onClick={() => setConfirm({ message: `Delete ${ `${c.first_name||""} ${c.last_name||""}`.trim() || c.name}?`, confirmLabel: "Delete", confirmColor: C.red, onConfirm: () => { db.deleteContact(c.id); setDetail(null); setConfirm(null); } })}
              style={{ display: "flex", alignItems: "center", gap: 6, background: C.red + "18", border: `1px solid ${C.red}44`, borderRadius: 8, color: C.red, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: F.body }}>
              <Ic n="trash" s={13} /> Delete
            </button>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "280px 1fr", gap: 18 }}>
          <div style={{ background: C.surface, border: `1px solid ${c.outcome === "sold" ? C.green + "44" : c.outcome === "lost" ? C.red + "44" : C.border}`, borderRadius: 16, padding: 22, alignSelf: "start" }}>
            <Avatar name={ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""} size={52} />
            <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 20, color: C.text, marginTop: 12, marginBottom: 2, letterSpacing: .3 }}>{ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""}</div>
            <div style={{ fontSize: 13, color: C.textSub, marginBottom: 14, fontFamily: F.body }}>{c.company}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
              <Pill label={c.type} color={TYPE_COLOR[c.type] || "#64748b"} />
              <Pill label={c.status} color={STATUS_COLOR[c.status] || "#64748b"} />
            </div>
            {[["mail","Email",c.email],["phone","Phone",c.phone],["person","Owner",c.owner],["person","Address",c.address]].map(([icon,lbl,val]) => (
              <div key={lbl} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
                <span style={{ color: C.textMuted, marginTop: 2 }}><Ic n={icon} s={13} /></span>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", fontFamily: F.body }}>{lbl}</div>
                  <div style={{ fontSize: 13, color: C.textSub, fontFamily: F.body }}>{val || "—"}</div>
                </div>
              </div>
            ))}
            {c.outcome && <OutcomeBanner outcome={c.outcome} onUndo={() => setOutcome(c.id, null)} />}
            {!c.outcome && (
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button onClick={() => setConfirm({ message: `Mark "${`${c.first_name||""} ${c.last_name||""}`.trim()||c.name}" as Sold?`, confirmLabel: "Mark Sold", confirmColor: C.green, onConfirm: () => { setOutcome(c.id, "sold"); setConfirm(null); } })}
                  style={{ flex: 1, background: C.green + "18", border: `1px solid ${C.green}44`, borderRadius: 8, color: C.green, padding: "8px 0", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: F.head }}>
                  ✅ Sold
                </button>
                <button onClick={() => setConfirm({ message: `Mark "${`${c.first_name||""} ${c.last_name||""}`.trim()||c.name}" as Lost?`, confirmLabel: "Mark Lost", confirmColor: C.red, onConfirm: () => { setOutcome(c.id, "lost"); setConfirm(null); } })}
                  style={{ flex: 1, background: C.red + "18", border: `1px solid ${C.red}44`, borderRadius: 8, color: C.red, padding: "8px 0", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: F.head }}>
                  ❌ Lost
                </button>
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <button onClick={() => setEmailModal(c)}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: C.tealDim, border: `1px solid ${C.tealBdr}`, borderRadius: 9, color: C.teal, padding: "10px 0", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: F.head, letterSpacing: .4 }}>
                <Ic n="mail" s={14} /> Initial Email
              </button>
            </div>
            {(() => {
              const noteTs = (notes[c.id] || []).map(n => n.ts).sort().reverse()[0];
              const lastActivity = noteTs || c.email_sent_at || c.created;
              const days = !c.outcome ? daysSince(lastActivity) : null;
              const stale = !c.outcome ? stalenessInfo(days) : null;
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                  {c.email_sent_at && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: C.tealDim, border: `1px solid ${C.tealBdr}`, borderRadius: 8 }}>
                      <span style={{ color: C.teal }}><Ic n="mail" s={13} /></span>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: C.teal, letterSpacing: 1, textTransform: "uppercase", fontFamily: F.body }}>Email Sent</div>
                        <div style={{ fontSize: 12, color: C.textSub, fontFamily: F.body }}>{new Date(c.email_sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                      </div>
                    </div>
                  )}
                  {stale && days > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: stale.bg, border: `1px solid ${stale.border}`, borderRadius: 8 }}>
                      <span style={{ fontSize: 14 }}>{days <= 3 ? "🟢" : days <= 7 ? "🟡" : "🔴"}</span>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: stale.color, letterSpacing: 1, textTransform: "uppercase", fontFamily: F.body }}>Last Activity</div>
                        <div style={{ fontSize: 12, color: C.textSub, fontFamily: F.body }}>{stale.label} — {days <= 3 ? "On track" : days <= 7 ? "Follow up soon" : "Needs attention!"}</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.textMuted, marginBottom: 6, fontFamily: F.body }}>
                <span>Pipeline Progress</span>
                <span style={{ color: C.teal, fontWeight: 700 }}>{doneCount}/{cTasks.length}</span>
              </div>
              <div style={{ display: "flex", gap: 3 }}>
                {cTasks.map(t => <div key={t.id} style={{ flex: 1, height: 5, borderRadius: 3, background: tasksDone[t.id] ? C.teal : C.border2 }} />)}
              </div>
            </div>
          </div>
          <div>
            <div style={{ display: "flex", background: C.surface, border: `1px solid ${C.border}`, borderRadius: "12px 12px 0 0", overflow: "hidden" }}>
              <TabBtn id="tasks" icon="tasks" label="Pipeline" count={cTasks.length - doneCount} />
              <TabBtn id="notes" icon="note"  label="Notes"    count={cNotes.length} />
            </div>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: "none", borderRadius: "0 0 12px 12px", padding: 16 }}>
              {detailTab === "tasks" && cTasks.map(t => (
                <PipelineStepRow key={t.id} task={t} done={!!tasksDone[t.id]} onToggle={() => {
                  const willBeDone = !tasksDone[t.id];
                  db.toggleTask(t.id, c.id);
                  if (willBeDone) addNote(c.id, { id: uid(), text: `☑️ Completed: ${t.title}`, author: "Admin", ts: new Date().toISOString() });
                }} locked={!!c.outcome} />
              ))}
              {detailTab === "notes" && (
                <div>
                  <NoteComposer onAdd={note => addNote(c.id, note)} currentUser={{ name: "Admin" }} />
                  {cNotes.length === 0 && <div style={{ color: C.textMuted, fontSize: 13, fontFamily: F.body, marginTop: 12 }}>No notes yet.</div>}
                  {cNotes.map(n => <NoteEntry key={n.id} note={n} onDelete={() => deleteNote(c.id, n.id)} />)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const NAV_ITEMS = [
    { id: "clients",  label: "All Clients",   icon: "users"  },
    { id: "calendar", label: "Calendar",      icon: "cal"    },
    { id: "calllog",  label: "Call Log",      icon: "phone"  },
    { id: "sold",     label: "Sold Jobs",     icon: "trophy" },
    { id: "lost",     label: "Lost Jobs",     icon: "xcirc"  },
    { id: "users",    label: "Team",          icon: "person" },
  ];
  const FIND_CLIENTS_URL = "https://www.google.com/search?q=local+businesses+in+Sacramento+County+CA";

  const sidebarContent = (
    <>
      <div style={{ padding: "24px 20px 18px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ color: C.teal }}><Ic n="shield" s={16} /></span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}><img src={logo} alt="JG Graphics" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} /><div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 19, color: "#f1f5f9", letterSpacing: .3 }}>JG <span style={{ color: C.teal }}>Graphics</span></div></div>
        </div>
        <div style={{ fontSize: 10, color: "#94a3b8", letterSpacing: 2, textTransform: "uppercase", fontFamily: F.body }}>Admin Dashboard</div>
      </div>
      <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 8 }}>
        {[["Contacts", contacts.filter(c => !isArchived(c)).length, C.teal],["Sold", totalSold, C.green],["Lost", totalLost, C.red]].map(([l,v,col]) => (
          <div key={l} style={{ flex: 1, background: "#ffffff15", borderRadius: 8, padding: "8px 6px", textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: col, fontFamily: F.head }}>{v}</div>
            <div style={{ fontSize: 9, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", fontFamily: F.body }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ padding: "10px 10px 0" }}>
        {NAV_ITEMS.map(n => (
          <button key={n.id} onClick={() => { setAdminTab(n.id); setDetail(null); setSearch(""); }}
            style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 12px", marginBottom: 3, background: adminTab === n.id ? C.tealDim : "transparent", border: `1px solid ${adminTab === n.id ? C.tealBdr : "transparent"}`, borderRadius: 9, color: adminTab === n.id ? C.teal : "#94a3b8", cursor: "pointer", fontSize: 13, fontWeight: adminTab === n.id ? 600 : 400, textAlign: "left", fontFamily: F.body }}>
            <Ic n={n.icon} s={15} />
            <span style={{ flex: 1 }}>{n.label}</span>
          </button>
        ))}
        <a href={FIND_CLIENTS_URL} target="_blank" rel="noopener noreferrer"
          style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 12px", marginBottom: 3, background: "transparent", border: "1px solid transparent", borderRadius: 9, color: "#94a3b8", cursor: "pointer", fontSize: 13, fontWeight: 400, textAlign: "left", fontFamily: F.body, textDecoration: "none", boxSizing: "border-box" }}>
          <Ic n="search" s={15} />
          <span style={{ flex: 1 }}>Find Clients</span>
          <Ic n="external" s={12} />
        </a>
      </div>
      {adminTab === "clients" && (
        <div style={{ padding: "8px 10px", flex: 1, overflowY: "auto" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#64748b", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6, padding: "0 6px", fontFamily: F.body }}>Filter by Rep</div>
          {[{ name: "all", label: "All Reps", count: contacts.length }, ...repsWithCounts.map(r => ({ name: r.name, label: r.name, count: r.count }))].map(item => (
            <button key={item.name} onClick={() => { setActiveRep(item.name); setDetail(null); }}
              style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 12px", marginBottom: 2, background: activeRep === item.name ? C.tealDim : "transparent", border: `1px solid ${activeRep === item.name ? C.tealBdr : "transparent"}`, borderRadius: 9, color: activeRep === item.name ? C.teal : "#94a3b8", cursor: "pointer", fontSize: 12, fontWeight: activeRep === item.name ? 600 : 400, textAlign: "left", fontFamily: F.body }}>
              {item.name !== "all" ? <Avatar name={item.name} size={20} /> : <span style={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", color: "inherit" }}><Ic n="users" s={13} /></span>}
              <span style={{ flex: 1 }}>{item.label}</span>
              <span style={{ background: "#ffffff18", color: "#94a3b8", fontSize: 10, fontWeight: 700, borderRadius: 10, padding: "1px 6px", fontFamily: F.body }}>{item.count}</span>
            </button>
          ))}
        </div>
      )}
      {adminTab !== "clients" && <div style={{ flex: 1 }} />}
      <button onClick={onLogout} style={{ margin: "0 10px 12px", display: "flex", alignItems: "center", gap: 8, background: "none", border: "1px solid #ffffff25", borderRadius: 9, color: "#64748b", cursor: "pointer", padding: "10px 12px", fontSize: 13, fontFamily: F.body, width: "calc(100% - 20px)" }}>
        <Ic n="logout" s={14} /> Sign Out
      </button>
    </>
  );

  const renderOutcomeFolder = (outcomeType) => {
    const isSold = outcomeType === "sold";
    const color  = isSold ? C.green : C.red;
    const icon   = isSold ? "trophy" : "xcirc";
    const label  = isSold ? "Sold Jobs" : "Lost Jobs";
    const allOutcome = contacts.filter(c => c.outcome === outcomeType);

    // Contacts marked within last 5 minutes are "pending"
    const now = Date.now();
    const pending  = allOutcome.filter(c => c.outcome_at && (now - new Date(c.outcome_at).getTime()) < 5 * 60 * 1000);
    const archived = allOutcome.filter(c => !c.outcome_at || (now - new Date(c.outcome_at).getTime()) >= 5 * 60 * 1000);

    return (
      <div>
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <span style={{ color }}><Ic n={icon} s={22} /></span>
            <h1 style={{ fontFamily: F.head, fontWeight: 700, fontSize: isMobile ? 22 : 26, color: C.text, margin: 0, letterSpacing: .3 }}>{label}</h1>
          </div>
          <p style={{ color: C.textMuted, fontSize: 12, fontFamily: F.body }}>{allOutcome.length} total · {archived.length} archived · {pending.length} pending</p>
        </div>

        {pending.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.amber, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10, fontFamily: F.body }}>⏳ Moving in 5 minutes…</div>
            <AdminContactGrid contacts={pending} tasksDone={tasksDone} notes={notes} onSelect={c => { setDetail(c); setDetailTab("tasks"); }} isMobile={isMobile} onNotify={notifyRep} slackStatus={slackStatus} />
          </div>
        )}

        {archived.length > 0 ? (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: color, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10, fontFamily: F.body }}>
              {isSold ? "✅ Sold" : "❌ Lost"} — {archived.length} contacts
            </div>
            <AdminContactGrid contacts={archived} tasksDone={tasksDone} notes={notes} onSelect={c => { setDetail(c); setDetailTab("tasks"); }} isMobile={isMobile} onNotify={notifyRep} slackStatus={slackStatus} />
          </div>
        ) : (
          pending.length === 0 && (
            <div style={{ color: C.textMuted, fontSize: 14, fontFamily: F.body, textAlign: "center", marginTop: 60 }}>
              No {label.toLowerCase()} yet.
            </div>
          )
        )}
      </div>
    );
  };

  const mainContent = (
    <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      {adminTab === "calendar" ? (
        <div style={{ padding: isMobile ? "16px" : 28, flex: 1 }}>
          <CalendarView
            events={events} contacts={contacts} users={users} isAdmin={true} currentUserName="Admin" isMobile={isMobile}
            onAdd={ev => { db.addEvent(ev); sendCalendarSlack(ev, "Admin"); }}
            onDelete={db.deleteEvent}
            onEdit={(id, patch) => db.updateEvent(id, patch)}
          />
        </div>
      ) : adminTab === "calllog" ? (
        <CallLogView
          currentUserName={currentUser?.name || "Admin"}
          reps={users}
          isMobile={isMobile}
          isAdmin={true}
          onAddToCRM={row => db.addContact({
            id: uid(), created: new Date().toISOString(), outcome: null,
            first_name: "", last_name: "", company: row.company_name || "",
            email: "", phone: row.phone_number || "", address: "",
            type: "Cold Lead", status: "Prospect", tags: ["Call Log"],
            owner: currentUser?.name || "Admin",
          })}
        />
      ) : (
        <div style={{ padding: isMobile ? "16px" : 28, flex: 1 }}>
          {adminTab === "users" ? renderUsersPage() :
           adminTab === "sold"  ? renderOutcomeFolder("sold") :
           adminTab === "lost"  ? renderOutcomeFolder("lost") : (
            detail ? renderContactDetail() : (
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
                  <div>
                    <h1 style={{ fontFamily: F.head, fontWeight: 700, fontSize: isMobile ? 22 : 26, color: C.text, margin: 0, letterSpacing: .3 }}>
                      {activeRep === "all" ? "All Clients" : `${activeRep}'s Clients`}
                    </h1>
                    <p style={{ color: C.textMuted, fontSize: 12, marginTop: 4, fontFamily: F.body }}>
                      {visibleContacts.length} contacts · {contacts.filter(c => c.outcome === "sold").length} sold · {contacts.filter(c => c.outcome === "lost").length} lost
                    </p>
                  </div>
                  <button onClick={() => setContactModal(true)} style={{ display: "flex", alignItems: "center", gap: 7, background: C.teal, border: "none", borderRadius: 9, color: "#fff", padding: "10px 18px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: F.head, letterSpacing: .5, flexShrink: 0 }}>
                    <Ic n="plus" s={14} /> New Contact
                  </button>
                </div>
                {activeRep === "all" ? (
                  reps.map(rep => {
                    const repContacts = visibleContacts.filter(c => c.owner === rep.name);
                    if (repContacts.length === 0) return null;
                    return (
                      <div key={rep.name} style={{ marginBottom: 32 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${C.border}` }}>
                          <Avatar name={rep.name} size={30} />
                          <div>
                            <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 16, color: C.text, letterSpacing: .3 }}>{rep.name}</div>
                            <div style={{ fontSize: 11, color: C.textMuted, fontFamily: F.body }}>
                              {repContacts.length} contacts · {repContacts.filter(c => c.outcome === "sold").length} sold · {repContacts.filter(c => c.outcome === "lost").length} lost
                            </div>
                          </div>
                        </div>
                        <AdminContactGrid contacts={repContacts} tasksDone={tasksDone} notes={notes} onSelect={c => { setDetail(c); setDetailTab("tasks"); }} isMobile={isMobile} onNotify={notifyRep} slackStatus={slackStatus} />
                      </div>
                    );
                  })
                ) : (
                  <AdminContactGrid contacts={visibleContacts} tasksDone={tasksDone} notes={notes} onSelect={c => { setDetail(c); setDetailTab("tasks"); }} isMobile={isMobile} onNotify={notifyRep} slackStatus={slackStatus} />
                )}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );

  if (isMobile) return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", color: "#d0d0d0" }}>
      <div style={{ background: C.sidebar, borderBottom: `1px solid ${C.border}`, padding: "14px 16px", flexShrink: 0 }}>
        {detail ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setDetail(null)} style={{ background: "none", border: "none", color: C.teal, cursor: "pointer", display: "flex", padding: 0 }}><Ic n="back" s={18} /></button>
            <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 17, color: C.text, flex: 1 }}>{`${detail.first_name||""} ${detail.last_name||""}`.trim()||detail.name||""}</div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><img src={logo} alt="JG Graphics" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} /><div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 18, color: C.text }}>JG <span style={{ color: C.teal }}>Graphics</span></div></div>
              <div style={{ fontSize: 10, color: C.amber, letterSpacing: 1.5, textTransform: "uppercase", fontFamily: F.body }}>Admin</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {adminTab === "clients" && (
                <select value={activeRep} onChange={e => { setActiveRep(e.target.value); setDetail(null); }} style={{ ...inp, width: "auto", fontSize: 12, padding: "7px 10px", background: "#ffffff" }}>
                  <option value="all">All Reps</option>
                  {reps.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
                </select>
              )}
              {adminTab === "clients" && (
                <button onClick={() => setContactModal(true)} style={{ background: C.teal, border: "none", borderRadius: 8, color: "#fff", padding: "8px 12px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: F.head, display: "flex", alignItems: "center", gap: 5 }}>
                  <Ic n="plus" s={14} /> New
                </button>
              )}
              {adminTab === "users" && (
                <button onClick={() => setUserModal("new")} style={{ background: C.teal, border: "none", borderRadius: 8, color: "#fff", padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: F.head, display: "flex", alignItems: "center", gap: 6 }}>
                  <Ic n="plus" s={14} /> New
                </button>
              )}
              <button onClick={onLogout} style={{ background: C.bg, border: `1px solid ${C.border2}`, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "7px 10px", display: "flex" }}><Ic n="logout" s={14} /></button>
            </div>
          </div>
        )}
      </div>
      {!detail && (
        <div style={{ background: C.sidebar, borderBottom: `1px solid ${C.border}`, display: "flex", flexShrink: 0 }}>
          {NAV_ITEMS.map(n => (
            <button key={n.id} onClick={() => { setAdminTab(n.id); setDetail(null); }}
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0", background: "none", border: "none", borderBottom: `2px solid ${adminTab === n.id ? C.teal : "transparent"}`, color: adminTab === n.id ? C.teal : C.textSub, cursor: "pointer", fontSize: 12, fontFamily: F.body }}>
              <Ic n={n.icon} s={15} />{n.label}
            </button>
          ))}
          <a href={FIND_CLIENTS_URL} target="_blank" rel="noopener noreferrer"
            style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0", background: "none", border: "none", borderBottom: "2px solid transparent", color: C.textSub, cursor: "pointer", fontSize: 12, fontFamily: F.body, textDecoration: "none" }}>
            <Ic n="search" s={15} />Find Clients
          </a>
        </div>
      )}
      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>{mainContent}</div>
      {contactModal && (
        <Modal title="New Contact" onClose={() => setContactModal(false)} isMobile>
          <AdminContactForm onSave={f => { const nc = { id: uid(), created: todayStr(), outcome: null, ...f }; db.addContact(nc); if (nc.owner) notifyRep(nc).catch(e => console.error('Slack notify error:', e)); }} />
        </Modal>
      )}
      {editContactModal && (
        <Modal title="Edit Contact" onClose={() => setEditContactModal(null)} isMobile>
          <AdminEditContactForm init={editContactModal} onSave={f => db.updateContact(editContactModal.id, f)} />
        </Modal>
      )}
      {userModal && (
        <Modal title={userModal === "new" ? "New User" : `Edit ${userModal.name}`} onClose={() => setUserModal(null)} isMobile>
          <UserForm init={userModal === "new" ? {} : userModal} isEdit={userModal !== "new"} onSave={f => { userModal === "new" ? addUser({ id: uid(), created: todayStr(), ...f }) : updateUser(userModal.id, f); }} />
        </Modal>
      )}
      {confirm && <Confirm {...confirm} onCancel={() => setConfirm(null)} />}
      {emailModal && <InitialEmailModal contact={emailModal} onClose={() => setEmailModal(null)} isMobile={true} onEmailSent={() => {
        const taskId = `${emailModal.id}_step1`;
        if (!tasksDone[taskId]) { db.toggleTask(taskId, emailModal.id); db.addNote(emailModal.id, { id: uid(), text: "☑️ Completed: Initial Email", author: "Admin", ts: new Date().toISOString() }); }
        db.updateContact(emailModal.id, { email_sent_at: new Date().toISOString() });
        setEmailModal(null);
      }} />}
    </div>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", color: "#d0d0d0" }}>
      <aside style={{ width: 240, background: C.sidebar, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", position: "fixed", height: "100vh", top: 0, left: 0, zIndex: 200 }}>
        {sidebarContent}
      </aside>
      <main style={{ marginLeft: 240, flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ background: C.sidebar, borderBottom: `1px solid ${C.border}`, padding: "13px 28px", display: "flex", alignItems: "center", gap: 10, position: "sticky", top: 0, zIndex: 100 }}>
          {adminTab === "clients" && (
            <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
              <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "#444" }}><Ic n="search" s={14} /></span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search all contacts…" style={{ ...inp, paddingLeft: 34, background: "#ffffff" }} />
            </div>
          )}
          <div style={{ flex: 1 }} />
          {adminTab === "clients" && !detail && (
            <button onClick={() => setContactModal(true)} style={{ display: "flex", alignItems: "center", gap: 7, background: C.teal, border: "none", borderRadius: 9, color: "#fff", padding: "9px 16px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: F.head, letterSpacing: .5 }}>
              <Ic n="plus" s={14} /> New Contact
            </button>
          )}
        </div>
        {mainContent}
      </main>
      {contactModal && (
        <Modal title="New Contact" onClose={() => setContactModal(false)} isMobile={false}>
          <AdminContactForm onSave={f => { const nc = { id: uid(), created: todayStr(), outcome: null, ...f }; db.addContact(nc); if (nc.owner) notifyRep(nc).catch(e => console.error('Slack notify error:', e)); }} />
        </Modal>
      )}
      {editContactModal && (
        <Modal title="Edit Contact" onClose={() => setEditContactModal(null)} isMobile={false}>
          <AdminEditContactForm init={editContactModal} onSave={f => db.updateContact(editContactModal.id, f)} />
        </Modal>
      )}
      {userModal && (
        <Modal title={userModal === "new" ? "Create New User" : `Edit ${userModal.name}`} onClose={() => setUserModal(null)} isMobile={false}>
          <UserForm init={userModal === "new" ? {} : userModal} isEdit={userModal !== "new"} onSave={f => { userModal === "new" ? addUser({ id: uid(), created: todayStr(), ...f }) : updateUser(userModal.id, f); }} />
        </Modal>
      )}
      {confirm && <Confirm {...confirm} onCancel={() => setConfirm(null)} />}
      {emailModal && <InitialEmailModal contact={emailModal} onClose={() => setEmailModal(null)} isMobile={false} onEmailSent={() => {
        const taskId = `${emailModal.id}_step1`;
        if (!tasksDone[taskId]) { db.toggleTask(taskId, emailModal.id); db.addNote(emailModal.id, { id: uid(), text: "☑️ Completed: Initial Email", author: "Admin", ts: new Date().toISOString() }); }
        db.updateContact(emailModal.id, { email_sent_at: new Date().toISOString() });
        setEmailModal(null);
      }} />}
    </div>
  );
}

// ─── Initial Email Modal ───────────────────────────────────────────────────────
function InitialEmailModal({ contact, onClose, isMobile, onEmailSent }) {
  const firstName = contact.first_name || contact.name?.split(" ")[0] || "there";
  const company   = contact.company || "your company";
  const email     = contact.email   || "";

  const TONES = [
    {
      label: "Friendly & Local",
      icon:  "👋",
      subject: `Custom apparel for ${company} — quick question`,
      body: `Hi ${firstName},\n\nI'm Jared with JG Graphics — we're a local screen printing and custom apparel shop with locations in Sacramento and Folsom. I came across ${company} and wanted to reach out personally.\n\nWe work with a lot of businesses in the area on branded uniforms, team gear, promotional items, and event apparel — basically anything with your logo on it. Whether you need 12 shirts or 1,200, we handle it in-house so the quality and turnaround are both solid.\n\nI'd love to learn a bit more about what you guys do and see if there's a fit. Would you be open to a quick 15-minute call sometime this week or next?\n\nThanks for your time — hope to connect soon.\n\nJared Gavard\nJG Graphics | Sacramento & Folsom\njared@jg.graphics`,
    },
    {
      label: "Direct & Value-Focused",
      icon:  "🎯",
      subject: `Local screen printing for ${company}`,
      body: `Hi ${firstName},\n\nMy name is Jared — I run JG Graphics, a custom apparel and screen printing company based right here in Sacramento (we also just opened a second location in Folsom).\n\nWe help local businesses with branded uniforms & workwear, promotional shirts and giveaway items, event and team apparel, and custom hats, bags, and accessories.\n\nNo middlemen, no outsourcing — everything is done in our shop, which means faster turnaround and better pricing than most online alternatives.\n\nWould you have 15 minutes for a quick call to talk through what you might need? Happy to work around your schedule.\n\nJared Gavard\nJG Graphics | Sacramento & Folsom\njared@jg.graphics`,
    },
    {
      label: "Short & Casual",
      icon:  "⚡",
      subject: `Quick question for ${company}`,
      body: `Hey ${firstName},\n\nJared here from JG Graphics — we're a local screen printing shop in Sacramento and Folsom.\n\nJust wanted to see if ${company} ever needs custom apparel, branded gear, or promo items. We do it all in-house and work with a lot of local businesses.\n\nWorth a quick chat?\n\nJared\nJG Graphics\njared@jg.graphics`,
    },
    {
      label: "Professional & Polished",
      icon:  "💼",
      subject: `Custom Branded Apparel — JG Graphics`,
      body: `Hi ${firstName},\n\nI hope this message finds you well. My name is Jared Gavard, and I'm the owner of JG Graphics, a full-service custom apparel and screen printing company with locations in Sacramento and Folsom, California.\n\nWe partner with businesses throughout the region to produce high-quality branded merchandise — from employee uniforms and workwear to promotional items and event apparel — all produced in-house for consistent quality and reliable turnaround times.\n\nI'd welcome the opportunity to learn more about ${company} and explore whether there's a way we can support your branding needs. Would you be available for a brief introductory call at your convenience?\n\nBest regards,\nJared Gavard\nJG Graphics | Sacramento & Folsom\njared@jg.graphics`,
    },
    {
      label: "Referral / Community Feel",
      icon:  "🤝",
      subject: `Fellow Sacramento business — let's connect`,
      body: `Hi ${firstName},\n\nAs a fellow Sacramento-area business owner, I always love connecting with other local companies — so I wanted to reach out.\n\nI run JG Graphics, a custom screen printing and apparel shop with locations in Sacramento and Folsom. We help local businesses put their brand on apparel, uniforms, promo items, hats, and more — all done in-house.\n\nIf ${company} ever needs custom branded gear — whether it's for your team, an event, or giveaways — I'd love to be the local shop you call first.\n\nHappy to jump on a quick call or even meet for coffee. Hope we can connect!\n\nJared Gavard\nJG Graphics | Sacramento & Folsom\njared@jg.graphics`,
    },
  ];

  const [selected, setSelected] = useState(null);
  const [copied, setCopied]     = useState(false);

  const handleSend = (tone) => {
    const mailtoUrl = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(tone.subject)}&body=${encodeURIComponent(tone.body)}`;
    window.location.href = mailtoUrl;
    if (onEmailSent) onEmailSent();
  };

  const handleCopy = (tone) => {
    navigator.clipboard.writeText(`Subject: ${tone.subject}\n\n${tone.body}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (onEmailSent) onEmailSent();
    });
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000000cc", zIndex: 2000, display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", padding: isMobile ? 0 : 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.surface, border: `1px solid ${C.border2}`, width: "100%", maxWidth: isMobile ? "100%" : 560, maxHeight: isMobile ? "92vh" : "90vh", overflow: "auto", borderRadius: isMobile ? "20px 20px 0 0" : 18, boxShadow: "0 -8px 40px #00000015" }}>
        {isMobile && <div style={{ width: 40, height: 4, background: C.border2, borderRadius: 2, margin: "12px auto 0" }} />}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: isMobile ? "16px 20px 0" : "22px 24px 0" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.text, fontFamily: F.head, letterSpacing: .3 }}>Initial Email</h2>
            <div style={{ fontSize: 12, color: C.textMuted, fontFamily: F.body, marginTop: 3 }}>Choose a tone for {firstName}{company ? ` at ${company}` : ""}</div>
          </div>
          <button onClick={onClose} style={{ background: C.bg, border: "none", color: C.textSub, cursor: "pointer", borderRadius: 8, padding: 8, display: "flex" }}><Ic n="x" s={16} /></button>
        </div>
        <div style={{ padding: isMobile ? "16px 20px 32px" : "20px 24px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
          {TONES.map((tone, i) => (
            <div key={i}>
              <button onClick={() => setSelected(selected === i ? null : i)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "13px 16px", background: selected === i ? C.tealDim : C.bg, border: `1px solid ${selected === i ? C.teal : C.border2}`, borderRadius: selected === i ? "10px 10px 0 0" : 10, cursor: "pointer", textAlign: "left", transition: "all .15s" }}>
                <span style={{ fontSize: 18 }}>{tone.icon}</span>
                <span style={{ fontFamily: F.head, fontWeight: 700, fontSize: 14, color: selected === i ? C.teal : C.text, flex: 1, letterSpacing: .2 }}>{tone.label}</span>
                <span style={{ fontSize: 12, color: C.textMuted, fontFamily: F.body }}>{selected === i ? "▲" : "▼"}</span>
              </button>
              {selected === i && (
                <div style={{ background: C.bg, border: `1px solid ${C.teal}`, borderTop: "none", borderRadius: "0 0 10px 10px", padding: "14px 16px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4, fontFamily: F.body }}>Subject</div>
                  <div style={{ fontSize: 13, color: C.textSub, fontFamily: F.body, marginBottom: 12, padding: "7px 10px", background: C.surface, borderRadius: 7, border: `1px solid ${C.border}` }}>{tone.subject}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4, fontFamily: F.body }}>Preview</div>
                  <div style={{ fontSize: 12, color: C.textSub, fontFamily: F.body, whiteSpace: "pre-wrap", lineHeight: 1.6, maxHeight: 180, overflowY: "auto", padding: "10px 12px", background: C.surface, borderRadius: 7, border: `1px solid ${C.border}`, marginBottom: 14 }}>{tone.body}</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {email ? (
                      <button onClick={() => handleSend(tone)}
                        style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: C.teal, border: "none", borderRadius: 9, color: "#fff", padding: "11px 0", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: F.head, letterSpacing: .5 }}>
                        <Ic n="send" s={14} /> Open in Mail
                      </button>
                    ) : (
                      <div style={{ flex: 1, fontSize: 12, color: C.amber, fontFamily: F.body, display: "flex", alignItems: "center", gap: 6, padding: "8px 0" }}>
                        ⚠️ No email on file — copy the message below
                      </div>
                    )}
                    <button onClick={() => handleCopy(tone)}
                      style={{ display: "flex", alignItems: "center", gap: 6, background: copied ? C.green + "18" : C.bg, border: `1px solid ${copied ? C.green + "44" : C.border2}`, borderRadius: 9, color: copied ? C.green : C.textSub, padding: "11px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: F.head }}>
                      {copied ? "✓ Copied" : "Copy"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AdminContactGrid({ contacts, tasksDone, notes, onSelect, isMobile, onNotify, slackStatus }) {
  if (contacts.length === 0) return <div style={{ color: C.textMuted, fontSize: 13, fontFamily: F.body }}>No contacts yet.</div>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(260px,1fr))", gap: isMobile ? 10 : 14 }}>
      {contacts.map(c => {
        const tasks = getTasksForContact(c.id);
        const doneCount = tasks.filter(t => tasksDone[t.id]).length;
        const noteCount = (notes[c.id] || []).length;
        return (
          <div key={c.id} onClick={() => onSelect(c)} style={{ background: c.outcome === "sold" ? C.green + "10" : c.outcome === "lost" ? C.red + "10" : C.surface, border: `1px solid ${c.outcome === "sold" ? C.green + "44" : c.outcome === "lost" ? C.red + "44" : C.border}`, borderRadius: 14, padding: 16, cursor: "pointer" }}>
            {c.outcome && (
              <div style={{ fontSize: 11, fontWeight: 700, color: c.outcome === "sold" ? C.green : C.red, display: "flex", alignItems: "center", gap: 5, marginBottom: 8, fontFamily: F.head }}>
                <Ic n={c.outcome === "sold" ? "trophy" : "xcirc"} s={12} />{c.outcome === "sold" ? "Sold" : "Lost"}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <Avatar name={ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 14, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", letterSpacing: .2 }}>{ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""}</div>
                <div style={{ fontSize: 11, color: C.textSub, fontFamily: F.body }}>{c.company}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 3, marginBottom: 8 }}>
              {tasks.map(t => <div key={t.id} style={{ flex: 1, height: 4, borderRadius: 3, background: tasksDone[t.id] ? C.teal : C.border2 }} />)}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.textMuted, fontFamily: F.body }}>
              <span>{doneCount}/{tasks.length} steps</span>
              {noteCount > 0 && <span style={{ color: C.teal }}>{noteCount} note{noteCount > 1 ? "s" : ""}</span>}
            </div>

          </div>
        );
      })}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// REP CRM (main app for sales reps)
// ═══════════════════════════════════════════════════════════════════════════════
function RepCRM({ currentUser, allContacts, tasksDone, notes, events, users, onLogout, isMobile, synced, db }) {
  // Reps only see their own contacts
  const myContacts = allContacts.filter(c => c.owner === currentUser.name);

  // Ticker so the UI reacts when the 5-minute grace period expires
  const nowRef = useRef(Date.now());
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const id = setInterval(() => { nowRef.current = Date.now(); forceUpdate(n => n + 1); }, 60_000);
    return () => clearInterval(id);
  }, []);

  // Contacts still in grace period (show on main page with undo option)
  const GRACE = 5 * 60 * 1000;
  const isPending = c => c.outcome && c.outcome_at && (nowRef.current - new Date(c.outcome_at).getTime()) < GRACE;
  // Active = no outcome OR still within grace period
  const activeContacts = myContacts.filter(c => !c.outcome || isPending(c));

  const [tab, setTab]             = useState("contacts");
  const [modal, setModal]         = useState(null);
  const [editItem, setEditItem]   = useState(null);
  const [confirm, setConfirm]     = useState(null);
  const [sheetStatus, setSheetStatus] = useState(null);
  const [search, setSearch]       = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filterType, setFilterType]   = useState("All");
  const [showDone, setShowDone]   = useState(false);
  const [detail, setDetail]       = useState(null);
  const [detailTab, setDetailTab] = useState("tasks");

  const addContact    = item => db.addContact({ id: uid(), created: todayStr(), outcome: null, owner: currentUser.name, ...item });
  const updateContact = (id, patch) => db.updateContact(id, patch);
  const deleteContact = id => { db.deleteContact(id); setDetail(null); };

  const autoNote = (contactId, text) => {
    db.addNote(contactId, { id: uid(), text, author: currentUser.name, ts: new Date().toISOString() });
  };

  const setOutcome = async (id, outcome) => {
    const outcome_at = outcome ? new Date().toISOString() : null;
    await db.updateContact(id, { outcome, outcome_at });
    if (outcome === "sold" || outcome === "lost") {
      const contact = allContacts.find(c => c.id === id);
      if (contact) {
        autoNote(id, outcome === "sold" ? "✅ Marked as Sold" : "❌ Marked as Lost");
        setSheetStatus("sending");
        const ok = await sendToSheet(contact, outcome);
        setSheetStatus(ok ? "sent" : "error");
        setTimeout(() => setSheetStatus(null), 3500);
      }
    } else if (outcome === null) {
      autoNote(id, "↩️ Outcome undone — returned to active");
    }
  };

  const toggleTask = (taskId, taskTitle) => {
    const cid = taskId.split("_step")[0];
    const willBeDone = !tasksDone[taskId];
    db.toggleTask(taskId, cid);
    if (willBeDone) autoNote(cid, `☑️ Completed: ${taskTitle}`);
  };

  const addNote    = (cid, note) => db.addNote(cid, note);
  const deleteNote = (cid, nid) => db.deleteNote(cid, nid);

  const closeModal = () => { setModal(null); setEditItem(null); };
  const openEdit   = item => { setEditItem(item); setModal("contact_edit"); };
  const switchTab  = id => { setTab(id); setDetail(null); setSearch(""); setShowFilters(false); };
  const [emailModal, setEmailModal] = useState(null);

  const allTasks  = activeContacts.flatMap(c => getTasksForContact(c.id).map(t => ({ ...t, contact: c })));
  const openTasks = allTasks.filter(t => !tasksDone[t.id]);
  const doneTasks = allTasks.filter(t => tasksDone[t.id]);
  const totalOpen = openTasks.length;

  const q = search.toLowerCase();
  const filtContacts = activeContacts.filter(c =>
    (!q || `${c.first_name||''} ${c.last_name||''}`.toLowerCase().includes(q) || (c.name||'').toLowerCase().includes(q) || c.company?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)) &&
    (filterType === "All" || c.type === filterType || c.status === filterType)
  );

  const soldContacts = myContacts.filter(c => c.outcome === "sold");
  const lostContacts = myContacts.filter(c => c.outcome === "lost");

  const NAV = [
    { id: "contacts", label: "Contacts", icon: "users",  count: activeContacts.filter(c => !c.outcome).length },
    { id: "calendar", label: "Calendar", icon: "cal",    count: (events||[]).filter(e => e.assigned_to === currentUser.name && e.date >= new Date().toISOString().slice(0,10)).length },
    { id: "tasks",    label: "Tasks",    icon: "tasks",   count: totalOpen },
    { id: "calllog",  label: "Call Log", icon: "phone",   count: null },
    { id: "sold",     label: "Sold",     icon: "trophy",  count: soldContacts.length },
    { id: "lost",     label: "Lost",     icon: "xcirc",   count: lostContacts.length },
  ];
  const FIND_CLIENTS_URL = "https://www.google.com/search?q=local+businesses+in+Sacramento+County+CA";

  // ── Contact Form ──────────────────────────────────────────────────────────
  const ContactForm = ({ init = {}, onSaveForm, isEdit = false }) => {
    const [f, setF] = useState({
      first_name: "", last_name: "", company: "", email: "", phone: "", address: "", type: "Hot Lead", status: "Active",
      ...init, tags: Array.isArray(init.tags) ? init.tags.join(", ") : (init.tags || ""),
    });
    const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));
    const [err, setErr] = useState("");
    const cols = isMobile ? "1fr" : "1fr 1fr";

    const checkDuplicate = () => {
      if (isEdit) return false; // skip duplicate check on edit
      if (f.email.trim()) {
        const emailMatch = allContacts.find(c => c.email?.toLowerCase().trim() === f.email.toLowerCase().trim());
        if (emailMatch) {
          const owner = emailMatch.owner || "another rep";
          const name  = `${emailMatch.first_name || ""} ${emailMatch.last_name || ""}`.trim() || emailMatch.name || "Someone";
          setErr(`${name} with this email already exists — owned by ${owner}.`);
          return true;
        }
      }
      if (f.phone.trim()) {
        const phoneMatch = allContacts.find(c => c.phone?.replace(/\D/g, "") === f.phone.replace(/\D/g, "") && c.phone.trim() !== "");
        if (phoneMatch) {
          const owner = phoneMatch.owner || "another rep";
          const name  = `${phoneMatch.first_name || ""} ${phoneMatch.last_name || ""}`.trim() || phoneMatch.name || "Someone";
          setErr(`${name} with this phone number already exists — owned by ${owner}.`);
          return true;
        }
      }
      if (f.company.trim()) {
        const companyMatch = allContacts.find(c => c.company?.toLowerCase().trim() === f.company.toLowerCase().trim() && c.company.trim() !== "");
        if (companyMatch) {
          const owner = companyMatch.owner || "another rep";
          const name  = `${companyMatch.first_name || ""} ${companyMatch.last_name || ""}`.trim() || companyMatch.name || "Someone";
          setErr(`${name} from ${f.company} already exists — owned by ${owner}.`);
          return true;
        }
      }
      return false;
    };

    return (
      <div style={{ display: "grid", gridTemplateColumns: cols, gap: "0 14px" }}>
        <Fld label="First Name"><input style={inp} value={f.first_name} onChange={set("first_name")} placeholder="Jane" /></Fld>
        <Fld label="Last Name"><input style={inp} value={f.last_name} onChange={set("last_name")} placeholder="Smith" /></Fld>
        <Fld label="Company"><input style={inp} value={f.company} onChange={e => { set("company")(e); setErr(""); }} placeholder="Acme Inc." /></Fld>
        <Fld label="Email"><input style={inp} value={f.email} onChange={e => { set("email")(e); setErr(""); }} placeholder="jane@co.com" /></Fld>
        <Fld label="Phone"><input style={inp} value={f.phone} onChange={e => { set("phone")(e); setErr(""); }} placeholder="555-0100" /></Fld>
        <Fld label="Address"><input style={inp} value={f.address} onChange={set("address")} placeholder="123 Main St, City, ST" /></Fld>
        <Fld label="Type">
          <select style={inp} value={f.type} onChange={set("type")}>{["Hot Lead","Warm Lead","Cold Lead"].map(o => <option key={o}>{o}</option>)}</select>
        </Fld>
        <Fld label="Status">
          <select style={inp} value={f.status} onChange={set("status")}>{["Active","Prospect","Inactive"].map(o => <option key={o}>{o}</option>)}</select>
        </Fld>
        <Fld label="Tags (comma-separated)" span2><input style={inp} value={f.tags} onChange={set("tags")} placeholder="Logo, Print…" /></Fld>
        {err && (
          <div style={{ gridColumn: "span 2", background: C.red + "18", border: `1px solid ${C.red}44`, borderRadius: 8, padding: "10px 12px", fontSize: 13, color: C.red, fontFamily: F.body }}>
            ⛔ {err}
          </div>
        )}
        <div style={{ gridColumn: "span 2" }}>
          <button style={btnP} onClick={() => {
            if (!f.first_name.trim()) { setErr("First name is required."); return; }
            if (checkDuplicate()) return;
            onSaveForm({ ...f, name: `${f.first_name} ${f.last_name}`.trim(), tags: f.tags.split(",").map(t => t.trim()).filter(Boolean) });
            closeModal();
          }}>Save Contact</button>
        </div>
      </div>
    );
  };

  // ── Outcome buttons ───────────────────────────────────────────────────────
  const OutcomeButtons = ({ c, size = "normal" }) => {
    if (c.outcome) return null;
    const small = size === "small";
    return (
      <div style={{ display: "flex", gap: 7 }}>
        <button onClick={e => { e.stopPropagation(); setConfirm({ message: `Mark "${ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""}" as Sold?`, confirmLabel: "Mark Sold", confirmColor: C.green, onConfirm: () => { setOutcome(c.id, "sold"); setConfirm(null); } }); }}
          style={{ display: "flex", alignItems: "center", gap: 5, background: C.green + "18", border: `1px solid ${C.green}44`, borderRadius: 8, color: C.green, padding: small ? "5px 10px" : "7px 12px", cursor: "pointer", fontSize: small ? 11 : 12, fontWeight: 700, fontFamily: F.head }}>
          <Ic n="trophy" s={small ? 12 : 13} /> Sold
        </button>
        <button onClick={e => { e.stopPropagation(); setConfirm({ message: `Mark "${ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""}" as Lost?`, confirmLabel: "Mark Lost", confirmColor: C.red, onConfirm: () => { setOutcome(c.id, "lost"); setConfirm(null); } }); }}
          style={{ display: "flex", alignItems: "center", gap: 5, background: C.red + "18", border: `1px solid ${C.red}44`, borderRadius: 8, color: C.red, padding: small ? "5px 10px" : "7px 12px", cursor: "pointer", fontSize: small ? 11 : 12, fontWeight: 700, fontFamily: F.head }}>
          <Ic n="xcirc" s={small ? 12 : 13} /> Lost
        </button>
      </div>
    );
  };

  // ── Render: Contacts List ─────────────────────────────────────────────────
  const renderOutcomeFolder = (outcomeType) => {
    const isSold = outcomeType === "sold";
    const color  = isSold ? C.green : C.red;
    const icon   = isSold ? "trophy" : "xcirc";
    const label  = isSold ? "Sold Jobs" : "Lost Jobs";
    const all    = myContacts.filter(c => c.outcome === outcomeType);
    const now    = Date.now();
    const pending  = all.filter(c => c.outcome_at && (now - new Date(c.outcome_at).getTime()) < 5 * 60 * 1000);
    const archived = all.filter(c => !c.outcome_at || (now - new Date(c.outcome_at).getTime()) >= 5 * 60 * 1000);
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ color }}><Ic n={icon} s={20} /></span>
            <h1 style={{ fontFamily: F.head, fontWeight: 700, fontSize: isMobile ? 20 : 24, color: C.text, margin: 0, letterSpacing: .3 }}>{label}</h1>
          </div>
          <p style={{ color: C.textMuted, fontSize: 12, fontFamily: F.body }}>{all.length} total · {archived.length} archived · {pending.length} pending</p>
        </div>
        {pending.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.amber, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10, fontFamily: F.body }}>⏳ Moving in 5 minutes…</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(280px,1fr))", gap: isMobile ? 10 : 14 }}>
              {pending.map(c => renderOutcomeCard(c))}
            </div>
          </div>
        )}
        {archived.length > 0 ? (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10, fontFamily: F.body }}>
              {isSold ? "✅ Sold" : "❌ Lost"} — {archived.length} contacts
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(280px,1fr))", gap: isMobile ? 10 : 14 }}>
              {archived.map(c => renderOutcomeCard(c))}
            </div>
          </div>
        ) : pending.length === 0 && (
          <div style={{ color: C.textMuted, fontSize: 14, fontFamily: F.body, textAlign: "center", marginTop: 60 }}>No {label.toLowerCase()} yet.</div>
        )}
      </div>
    );
  };

  const renderOutcomeCard = (c) => {
    const isSold = c.outcome === "sold";
    const color  = isSold ? C.green : C.red;
    return (
      <div key={c.id} onClick={() => { setDetail(c); setDetailTab("tasks"); }}
        style={{ background: color + "10", border: `1px solid ${color}44`, borderRadius: 14, padding: isMobile ? 14 : 18, cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, fontSize: 11, fontWeight: 700, color, fontFamily: F.head }}>
          <Ic n={isSold ? "trophy" : "xcirc"} s={13} />{isSold ? "Sold" : "Lost"}
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <Avatar name={(`${c.first_name||""} ${c.last_name||""}`.trim()||c.name||"?")} size={38} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 15, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{`${c.first_name||""} ${c.last_name||""}`.trim()||c.name}</div>
            <div style={{ fontSize: 12, color: C.textSub, fontFamily: F.body }}>{c.company}</div>
            {c.email && <div style={{ fontSize: 11, color: C.textMuted, fontFamily: F.body, marginTop: 2 }}>{c.email}</div>}
          </div>
        </div>
        {c.outcome_at && (
          <div style={{ fontSize: 10, color: C.textMuted, fontFamily: F.body, marginTop: 10 }}>
            {isSold ? "Sold" : "Lost"} on {new Date(c.outcome_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </div>
        )}
      </div>
    );
  };

  const renderContactsList = () => (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontFamily: F.head, fontWeight: 700, fontSize: isMobile ? 22 : 26, color: C.text, margin: 0, letterSpacing: .3 }}>My Contacts</h1>
        <p style={{ color: C.textMuted, fontSize: 12, marginTop: 4, fontFamily: F.body }}>
          {filtContacts.length} contacts · {myContacts.filter(c => c.outcome === "sold").length} sold · {myContacts.filter(c => c.outcome === "lost").length} lost
        </p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(280px,1fr))", gap: isMobile ? 10 : 14 }}>
        {filtContacts.map(c => {
          const tasks = getTasksForContact(c.id);
          const doneCount = tasks.filter(t => tasksDone[t.id]).length;
          const noteCount = (notes[c.id] || []).length;
          // Last activity = most recent note timestamp or email_sent_at
          const noteTs = (notes[c.id] || []).map(n => n.ts).sort().reverse()[0];
          const lastActivity = noteTs || c.email_sent_at || c.created;
          const days = !c.outcome ? daysSince(lastActivity) : null;
          const stale = !c.outcome ? stalenessInfo(days) : null;
          return (
            <div key={c.id} onClick={() => { setDetail(c); setDetailTab("tasks"); }}
              style={{ background: c.outcome === "sold" ? C.green + "10" : c.outcome === "lost" ? C.red + "10" : C.surface, border: `1px solid ${c.outcome === "sold" ? C.green + "44" : c.outcome === "lost" ? C.red + "44" : C.border}`, borderRadius: 14, padding: isMobile ? 14 : 18, cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
              {c.outcome && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, fontSize: 11, fontWeight: 700, color: c.outcome === "sold" ? C.green : C.red, fontFamily: F.head }}>
                  <Ic n={c.outcome === "sold" ? "trophy" : "xcirc"} s={13} />
                  {c.outcome === "sold" ? "Sold" : "Lost"}
                  <button onClick={e => { e.stopPropagation(); setOutcome(c.id, null); }} style={{ marginLeft: "auto", background: "none", border: `1px solid ${C.border2}`, borderRadius: 6, color: C.textMuted, cursor: "pointer", padding: "2px 8px", fontSize: 10, display: "flex", alignItems: "center", gap: 4, fontFamily: F.body }}>
                    <Ic n="undo" s={10} /> Undo
                  </button>
                </div>
              )}
              <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
                <Avatar name={ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""} size={isMobile ? 38 : 42} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 15, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", letterSpacing: .2 }}>{ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""}</div>
                  <div style={{ fontSize: 12, color: C.textSub, marginTop: 1, fontFamily: F.body }}>{c.company}</div>
                  <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
                    <Pill label={c.type} color={TYPE_COLOR[c.type] || "#64748b"} />
                    <Pill label={c.status} color={STATUS_COLOR[c.status] || "#64748b"} />
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.textMuted, marginBottom: 4, fontFamily: F.body }}>
                  <span>Pipeline</span>
                  <span style={{ color: doneCount === tasks.length ? C.teal : C.textMuted, fontWeight: 600 }}>{doneCount}/{tasks.length}</span>
                </div>
                <div style={{ display: "flex", gap: 3 }}>
                  {tasks.map(t => <div key={t.id} style={{ flex: 1, height: 4, borderRadius: 3, background: tasksDone[t.id] ? C.teal : C.border2 }} />)}
                </div>
              </div>
              {c.tags?.length > 0 && <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>{c.tags.map(t => <Tag key={t} label={t} />)}</div>}
              {c.email_sent_at && (
                <div style={{ fontSize: 11, color: C.textMuted, fontFamily: F.body, marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
                  <Ic n="mail" s={11} />
                  <span>Email sent {new Date(c.email_sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                <div style={{ fontSize: 11, color: C.textSub, fontFamily: F.body, display: "flex", alignItems: "center", gap: 8 }}>
                  {noteCount > 0 && <span style={{ color: C.teal }}>{noteCount} note{noteCount > 1 ? "s" : ""}</span>}
                  {stale && (
                    <span style={{ background: stale.bg, border: `1px solid ${stale.border}`, color: stale.color, borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700, fontFamily: F.body }}>
                      {days === 0 ? "Active today" : `${stale.label} inactive`}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }} onClick={e => e.stopPropagation()}>
                  {!c.outcome && <OutcomeButtons c={c} size="small" />}
                  <button onClick={() => openEdit(c)} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", display: "flex", padding: 4 }}><Ic n="edit" s={14} /></button>
                  <button onClick={() => setConfirm({ message: `Delete ${ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""}?`, confirmLabel: "Delete", confirmColor: C.red, onConfirm: () => { deleteContact(c.id); setConfirm(null); } })} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", display: "flex", padding: 4 }}><Ic n="trash" s={14} /></button>
                </div>
              </div>
            </div>
          );
        })}
        {filtContacts.length === 0 && <div style={{ color: C.textMuted, fontSize: 14, fontFamily: F.body }}>No contacts yet. Add your first one!</div>}
      </div>
    </div>
  );

  // ── Render: Contact Detail ────────────────────────────────────────────────
  const renderContactDetail = () => {
    const c = activeContacts.find(x => x.id === detail.id) || myContacts.find(x => x.id === detail.id) || detail;
    const cTasks = getTasksForContact(c.id);
    const cNotes = notes[c.id] || [];
    const doneCount = cTasks.filter(t => tasksDone[t.id]).length;
    const locked = !!c.outcome;

    const TabBtn = ({ id, icon, label, count }) => (
      <button onClick={() => setDetailTab(id)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0", background: detailTab === id ? C.tealDim : "transparent", border: "none", borderBottom: `2px solid ${detailTab === id ? C.teal : "transparent"}`, color: detailTab === id ? C.teal : C.textMuted, cursor: "pointer", fontSize: 13, fontWeight: detailTab === id ? 700 : 400, fontFamily: F.body }}>
        <Ic n={icon} s={14} />{label}
        {count > 0 && <span style={{ background: detailTab === id ? C.teal : "#cbd5e1", color: detailTab === id ? "#fff" : C.textMuted, fontSize: 10, fontWeight: 700, borderRadius: 10, padding: "1px 7px" }}>{count}</span>}
      </button>
    );

    return (
      <div>
        {!isMobile && (
          <button onClick={() => setDetail(null)} style={{ background: "none", border: "none", color: C.teal, cursor: "pointer", fontSize: 13, marginBottom: 18, display: "flex", alignItems: "center", gap: 6, fontWeight: 600, padding: 0, fontFamily: F.body }}>
            <Ic n="back" s={14} /> Back to Contacts
          </button>
        )}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "280px 1fr", gap: 18 }}>
          <div style={{ background: C.surface, border: `1px solid ${c.outcome === "sold" ? C.green + "44" : c.outcome === "lost" ? C.red + "44" : C.border}`, borderRadius: 16, padding: isMobile ? 18 : 22, alignSelf: "start" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <Avatar name={ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""} size={isMobile ? 46 : 52} />
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => openEdit(c)} style={{ background: C.bg, border: `1px solid ${C.border2}`, color: C.textSub, cursor: "pointer", borderRadius: 7, padding: 8, display: "flex" }}><Ic n="edit" s={14} /></button>
                <button onClick={() => setConfirm({ message: `Delete ${ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""}?`, confirmLabel: "Delete", confirmColor: C.red, onConfirm: () => { deleteContact(c.id); setConfirm(null); } })} style={{ background: C.bg, border: `1px solid ${C.border2}`, color: C.textSub, cursor: "pointer", borderRadius: 7, padding: 8, display: "flex" }}><Ic n="trash" s={14} /></button>
              </div>
            </div>
            <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 20, color: C.text, marginBottom: 2, letterSpacing: .3 }}>{ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""}</div>
            <div style={{ fontSize: 13, color: C.textSub, marginBottom: 14, fontFamily: F.body }}>{c.company}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
              <Pill label={c.type} color={TYPE_COLOR[c.type] || "#64748b"} />
              <Pill label={c.status} color={STATUS_COLOR[c.status] || "#64748b"} />
            </div>
            {[["mail","Email",c.email],["phone","Phone",c.phone],["person","Address",c.address]].map(([icon,lbl,val]) => (
              <div key={lbl} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
                <span style={{ color: C.textMuted, marginTop: 2 }}><Ic n={icon} s={13} /></span>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", fontFamily: F.body }}>{lbl}</div>
                  <div style={{ fontSize: 13, color: C.textSub, fontFamily: F.body }}>{val || "—"}</div>
                </div>
              </div>
            ))}
            {c.tags?.length > 0 && <div style={{ marginTop: 8 }}><div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6, fontFamily: F.body }}>Tags</div><div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{c.tags.map(t => <Tag key={t} label={t} />)}</div></div>}
            {(() => {
              const noteTs = (notes[c.id] || []).map(n => n.ts).sort().reverse()[0];
              const lastActivity = noteTs || c.email_sent_at || c.created;
              const days = !c.outcome ? daysSince(lastActivity) : null;
              const stale = !c.outcome ? stalenessInfo(days) : null;
              return (
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                  {c.email_sent_at && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: C.tealDim, border: `1px solid ${C.tealBdr}`, borderRadius: 8 }}>
                      <span style={{ color: C.teal }}><Ic n="mail" s={13} /></span>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: C.teal, letterSpacing: 1, textTransform: "uppercase", fontFamily: F.body }}>Email Sent</div>
                        <div style={{ fontSize: 12, color: C.textSub, fontFamily: F.body }}>{new Date(c.email_sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                      </div>
                    </div>
                  )}
                  {stale && days > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: stale.bg, border: `1px solid ${stale.border}`, borderRadius: 8 }}>
                      <span style={{ color: stale.color, fontSize: 14 }}>{days <= 3 ? "🟢" : days <= 7 ? "🟡" : "🔴"}</span>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: stale.color, letterSpacing: 1, textTransform: "uppercase", fontFamily: F.body }}>Last Activity</div>
                        <div style={{ fontSize: 12, color: C.textSub, fontFamily: F.body }}>{stale.label} — {days <= 3 ? "On track" : days <= 7 ? "Follow up soon" : "Needs attention!"}</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            <div style={{ marginTop: 14 }}>
              <button onClick={() => setEmailModal(c)}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: C.tealDim, border: `1px solid ${C.tealBdr}`, borderRadius: 9, color: C.teal, padding: "10px 0", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: F.head, letterSpacing: .4 }}>
                <Ic n="mail" s={14} /> Initial Email
              </button>
            </div>
            <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.textMuted, marginBottom: 6, fontFamily: F.body }}>
                <span>Pipeline Progress</span>
                <span style={{ color: C.teal, fontWeight: 700 }}>{doneCount}/{cTasks.length}</span>
              </div>
              <div style={{ display: "flex", gap: 3, marginBottom: 14 }}>
                {cTasks.map(t => <div key={t.id} style={{ flex: 1, height: 5, borderRadius: 3, background: tasksDone[t.id] ? C.teal : C.border2 }} />)}
              </div>
              {c.outcome ? (
                <OutcomeBanner outcome={c.outcome} onUndo={() => setOutcome(c.id, null)} />
              ) : (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8, fontFamily: F.body }}>Mark Outcome</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => setConfirm({ message: `Mark "${ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""}" as Sold?`, confirmLabel: "Mark Sold", confirmColor: C.green, onConfirm: () => { setOutcome(c.id, "sold"); setConfirm(null); } })}
                      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: C.green + "18", border: `1px solid ${C.green}44`, borderRadius: 9, color: C.green, padding: "10px 0", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: F.head }}>
                      <Ic n="trophy" s={14} /> Sold
                    </button>
                    <button onClick={() => setConfirm({ message: `Mark "${ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""}" as Lost?`, confirmLabel: "Mark Lost", confirmColor: C.red, onConfirm: () => { setOutcome(c.id, "lost"); setConfirm(null); } })}
                      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: C.red + "18", border: `1px solid ${C.red}44`, borderRadius: 9, color: C.red, padding: "10px 0", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: F.head }}>
                      <Ic n="xcirc" s={14} /> Lost
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div>
            <div style={{ display: "flex", background: C.surface, border: `1px solid ${C.border}`, borderRadius: "12px 12px 0 0", overflow: "hidden" }}>
              <TabBtn id="tasks" icon="tasks" label="Pipeline" count={cTasks.length - doneCount} />
              <TabBtn id="notes" icon="note"  label="Notes"    count={cNotes.length} />
            </div>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: "none", borderRadius: "0 0 12px 12px", padding: 16 }}>
              {detailTab === "tasks" && (
                <div>
                  {locked && <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12, padding: "8px 12px", background: C.bg, borderRadius: 8, fontFamily: F.body }}>Pipeline locked after outcome is set. Undo to re-open.</div>}
                  {cTasks.map(t => <PipelineStepRow key={t.id} task={t} done={!!tasksDone[t.id]} onToggle={() => toggleTask(t.id, t.title)} locked={locked} />)}
                </div>
              )}
              {detailTab === "notes" && (
                <div>
                  <NoteComposer onAdd={note => addNote(c.id, note)} currentUser={currentUser} />
                  {cNotes.length === 0 && <div style={{ color: C.textMuted, fontSize: 13, fontFamily: F.body }}>No notes yet.</div>}
                  {cNotes.map(n => <NoteEntry key={n.id} note={n} onDelete={() => deleteNote(c.id, n.id)} />)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Render: Tasks ─────────────────────────────────────────────────────────
  const renderTasks = () => {
    const active = activeContacts.filter(c => !c.outcome);
    return (
      <div>
        <h1 style={{ fontFamily: F.head, fontWeight: 700, fontSize: isMobile ? 22 : 26, color: C.text, margin: "0 0 4px", letterSpacing: .3 }}>Pipeline Tasks</h1>
        <p style={{ color: C.textMuted, fontSize: 12, marginBottom: 22, fontFamily: F.body }}>{openTasks.length} open · {doneTasks.length} completed</p>
        {active.map(c => {
          const open = getTasksForContact(c.id).filter(t => !tasksDone[t.id]);
          if (open.length === 0) return null;
          return (
            <div key={c.id} style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Avatar name={ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""} size={22} />
                <span style={{ fontSize: 13, fontWeight: 600, color: C.textSub, fontFamily: F.body }}>{ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""}</span>
                <span style={{ fontSize: 11, color: C.textMuted, fontFamily: F.body }}>· {c.company}</span>
              </div>
              {open.map(t => <PipelineStepRow key={t.id} task={t} done={false} onToggle={() => toggleTask(t.id, t.title)} locked={false} />)}
            </div>
          );
        })}
        {openTasks.length === 0 && <div style={{ color: C.textMuted, fontSize: 13, fontFamily: F.body }}>All steps complete! 🎉</div>}
        <div style={{ marginTop: 8 }}>
          <button onClick={() => setShowDone(p => !p)} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 6, padding: 0, fontFamily: F.body }}>
            {showDone ? "▾" : "▸"} Completed · {doneTasks.length}
          </button>
          {showDone && activeContacts.map(c => {
            const done = getTasksForContact(c.id).filter(t => tasksDone[t.id]);
            if (done.length === 0) return null;
            return (
              <div key={c.id} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <Avatar name={ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""} size={22} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.textSub, fontFamily: F.body }}>{ `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || ""}</span>
                </div>
                {done.map(t => <PipelineStepRow key={t.id} task={t} done={true} onToggle={() => toggleTask(t.id, t.title)} locked={!!c.outcome} />)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const FilterBar = () => (
    <div style={{ display: "flex", gap: 8, padding: isMobile ? "10px 16px" : 0, background: isMobile ? C.sidebar : "transparent", borderBottom: isMobile ? `1px solid ${C.border}` : "none" }}>
      <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...inp, width: "auto", fontSize: 12, padding: "7px 10px", background: "#ffffff" }}>
        <option value="All">All types</option>
        {["Hot Lead","Warm Lead","Cold Lead","Active","Prospect","Inactive"].map(o => <option key={o}>{o}</option>)}
      </select>
    </div>
  );

  const sharedModals = (
    <>
      {modal === "contact_new"  && <Modal title="New Contact" onClose={closeModal} isMobile={isMobile}><ContactForm onSaveForm={addContact} /></Modal>}
      {modal === "contact_edit" && editItem && <Modal title="Edit Contact" onClose={closeModal} isMobile={isMobile}><ContactForm init={editItem} isEdit={true} onSaveForm={f => updateContact(editItem.id, f)} /></Modal>}
      {emailModal && <InitialEmailModal contact={emailModal} onClose={() => setEmailModal(null)} isMobile={isMobile} onEmailSent={() => {
        const taskId = `${emailModal.id}_step1`;
        if (!tasksDone[taskId]) { toggleTask(taskId, "Initial Email"); }
        updateContact(emailModal.id, { email_sent_at: new Date().toISOString() });
        setEmailModal(null);
      }} />}
      {confirm && <Confirm {...confirm} onCancel={() => setConfirm(null)} />}
      <SheetToast status={sheetStatus} />
    </>
  );

  // ── DESKTOP ───────────────────────────────────────────────────────────────
  if (!isMobile) return (
    <>
      <div style={{ display: "flex", minHeight: "100vh", color: "#d0d0d0" }}>
        <aside style={{ width: 220, background: C.sidebar, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", position: "fixed", height: "100vh", top: 0, left: 0, zIndex: 200 }}>
          <div style={{ padding: "24px 20px 18px", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><img src={logo} alt="JG Graphics" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} /><div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 19, color: "#f1f5f9", letterSpacing: .3 }}>JG <span style={{ color: C.teal }}>Graphics</span></div></div>
            <div style={{ fontSize: 10, color: "#444", letterSpacing: 2, textTransform: "uppercase", marginTop: 3, fontFamily: F.body }}>Sales CRM</div>
          </div>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
            <Avatar name={currentUser.name} size={34} />
            <div>
              <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 14, color: C.text, letterSpacing: .2 }}>{currentUser.name}</div>
              <div style={{ fontSize: 10, color: C.teal, letterSpacing: 1, textTransform: "uppercase", fontFamily: F.body }}>Sales Rep</div>
            </div>
          </div>
          <nav style={{ padding: "12px 10px", flex: 1 }}>
            {NAV.map(n => (
              <button key={n.id} onClick={() => switchTab(n.id)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 12px", marginBottom: 3, background: tab === n.id ? C.tealDim : "transparent", border: `1px solid ${tab === n.id ? C.tealBdr : "transparent"}`, borderRadius: 9, color: tab === n.id ? C.teal : "#94a3b8", cursor: "pointer", fontSize: 13, fontWeight: tab === n.id ? 600 : 400, textAlign: "left", fontFamily: F.body }}>
                <Ic n={n.icon} s={15} />
                <span style={{ flex: 1 }}>{n.label}</span>
                <span style={{ background: "#ffffff18", color: "#94a3b8", fontSize: 10, fontWeight: 700, borderRadius: 10, padding: "1px 7px", fontFamily: F.body }}>{n.count}</span>
              </button>
            ))}
            <a href={FIND_CLIENTS_URL} target="_blank" rel="noopener noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 12px", marginBottom: 3, background: "transparent", border: "1px solid transparent", borderRadius: 9, color: "#94a3b8", cursor: "pointer", fontSize: 13, fontWeight: 400, textAlign: "left", fontFamily: F.body, textDecoration: "none", boxSizing: "border-box" }}>
              <Ic n="search" s={15} />
              <span style={{ flex: 1 }}>Find Clients</span>
              <Ic n="external" s={12} />
            </a>
          </nav>
          <div style={{ padding: "8px 10px", borderTop: `1px solid #1e1e1e` }}>
            <button onClick={onLogout} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "none", border: `1px solid ${C.border2}`, borderRadius: 9, color: C.textMuted, cursor: "pointer", padding: "10px 12px", fontSize: 13, fontFamily: F.body }}>
              <Ic n="logout" s={14} /> Sign Out
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, padding: "0 4px", fontSize: 11, color: synced ? C.teal : C.amber, fontFamily: F.body }}>
              <Ic n="cloud" s={13} />{synced ? "Data saved" : "Saving…"}
            </div>
          </div>
        </aside>
        <main style={{ marginLeft: 220, flex: 1, display: "flex", flexDirection: "column" }}>
          <div style={{ background: C.sidebar, borderBottom: `1px solid ${C.border}`, padding: "13px 28px", display: "flex", alignItems: "center", gap: 10, position: "sticky", top: 0, zIndex: 100 }}>
            <div style={{ position: "relative", width: 260 }}>
              <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "#444" }}><Ic n="search" s={14} /></span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search my contacts…" style={{ ...inp, paddingLeft: 34, background: "#ffffff" }} />
            </div>
            {tab === "contacts" && !detail && <FilterBar />}
            {tab === "contacts" && (
              <div style={{ marginLeft: "auto" }}>
                <button onClick={() => { setEditItem(null); setModal("contact_new"); }} style={{ display: "flex", alignItems: "center", gap: 7, background: C.teal, border: "none", borderRadius: 9, color: "#fff", padding: "9px 16px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: F.head, letterSpacing: .5 }}>
                  <Ic n="plus" s={14} /> New Contact
                </button>
              </div>
            )}
          </div>
          <div style={{ padding: 28, flex: 1 }}>
            {tab === "contacts" && !detail && renderContactsList()}
            {tab === "contacts" && detail  && renderContactDetail()}
            {tab === "tasks"    && renderTasks()}
            {tab === "calendar" && (
              <div style={{ padding: isMobile ? 16 : 28, flex: 1 }}>
                <CalendarView
                  events={events||[]} contacts={myContacts} users={users||[]} isAdmin={false} currentUserName={currentUser.name} isMobile={isMobile}
                  onAdd={ev => { const e = { ...ev, id: uid(), created_by: currentUser.name, created: new Date().toISOString() }; db.addEvent(e); sendCalendarSlack(e, currentUser.name); }}
                  onDelete={db.deleteEvent}
                  onEdit={(id, patch) => db.updateEvent(id, patch)}
                />
              </div>
            )}
            {tab === "sold"  && renderOutcomeFolder("sold")}
            {tab === "lost"  && renderOutcomeFolder("lost")}
            {tab === "calllog" && (
              <CallLogView
                currentUserName={currentUser.name}
                reps={users}
                isMobile={isMobile}
                onAddToCRM={row => addContact({
                  company: row.company_name || "", phone: row.phone_number || "",
                  first_name: "", last_name: "", email: "", address: "",
                  type: "Cold Lead", status: "Prospect", tags: ["Call Log"],
                })}
              />
            )}
          </div>
        </main>
      </div>
      {sharedModals}
    </>
  );

  // ── MOBILE ────────────────────────────────────────────────────────────────
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", color: "#d0d0d0" }}>
        <div style={{ background: C.sidebar, borderBottom: `1px solid ${C.border}`, padding: "14px 16px 12px", flexShrink: 0 }}>
          {detail ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button onClick={() => setDetail(null)} style={{ background: "none", border: "none", color: C.teal, cursor: "pointer", display: "flex", padding: 0 }}><Ic n="back" s={18} /></button>
              <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 17, color: C.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: .2 }}>{`${detail.first_name||""} ${detail.last_name||""}`.trim()||detail.name||""}</div>
              <button onClick={() => openEdit(myContacts.find(x => x.id === detail.id) || detail)} style={{ background: C.tealDim, border: `1px solid ${C.tealBdr}`, color: C.teal, borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex" }}><Ic n="edit" s={15} /></button>
            </div>
          ) : (
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: tab === "contacts" ? 10 : 0 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}><img src={logo} alt="JG Graphics" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} /><div style={{ fontFamily: F.head, fontWeight: 700, fontSize: 18, color: C.text, letterSpacing: .3 }}>JG <span style={{ color: C.teal }}>Graphics</span></div></div>
                  <div style={{ fontSize: 10, color: C.textMuted, fontFamily: F.body }}>{currentUser.name}</div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {tab === "contacts" && <>
                    <button onClick={() => setShowFilters(f => !f)} style={{ background: showFilters ? C.tealDim : "#f1f5f9", border: `1px solid ${showFilters ? C.tealBdr : C.border2}`, color: showFilters ? C.teal : C.textSub, borderRadius: 8, padding: "7px 10px", cursor: "pointer", display: "flex" }}><Ic n="filter" s={13} /></button>
                    <button onClick={() => { setEditItem(null); setModal("contact_new"); }} style={{ background: C.teal, border: "none", borderRadius: 8, color: "#fff", padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: F.head, display: "flex", alignItems: "center", gap: 6 }}>
                      <Ic n="plus" s={14} /> New
                    </button>
                  </>}
                  <button onClick={onLogout} style={{ background: C.bg, border: `1px solid ${C.border2}`, borderRadius: 8, color: C.textMuted, cursor: "pointer", padding: "7px 10px", display: "flex" }}><Ic n="logout" s={14} /></button>
                </div>
              </div>
              {tab === "contacts" && (
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "#444" }}><Ic n="search" s={14} /></span>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search my contacts…" style={{ ...inp, paddingLeft: 34, background: "#ffffff" }} />
                </div>
              )}
            </div>
          )}
        </div>
        {showFilters && !detail && tab === "contacts" && <FilterBar />}
        <div style={{ flex: 1, padding: "16px 16px 8px", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          {tab === "contacts" && !detail && renderContactsList()}
          {tab === "contacts" && detail  && renderContactDetail()}
          {tab === "tasks"    && renderTasks()}
            {tab === "calendar" && (
              <div style={{ padding: 16, flex: 1 }}>
                <CalendarView
                  events={events||[]} contacts={myContacts} users={users||[]} isAdmin={false} currentUserName={currentUser.name} isMobile={true}
                  onAdd={ev => { const e = { ...ev, id: uid(), created_by: currentUser.name, created: new Date().toISOString() }; db.addEvent(e); sendCalendarSlack(e, currentUser.name); }}
                  onDelete={db.deleteEvent}
                  onEdit={(id, patch) => db.updateEvent(id, patch)}
                />
              </div>
            )}
            {tab === "sold"  && renderOutcomeFolder("sold")}
            {tab === "lost"  && renderOutcomeFolder("lost")}
            {tab === "calllog" && (
              <CallLogView
                currentUserName={currentUser.name}
                reps={users}
                isMobile={true}
                onAddToCRM={row => addContact({
                  company: row.company_name || "", phone: row.phone_number || "",
                  first_name: "", last_name: "", email: "", address: "",
                  type: "Cold Lead", status: "Prospect", tags: ["Call Log"],
                })}
              />
            )}
        </div>
        <div style={{ background: C.sidebar, borderTop: `1px solid ${C.border}`, display: "flex", flexShrink: 0, zIndex: 200, paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
          {NAV.map(n => (
            <button key={n.id} onClick={() => switchTab(n.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "10px 0 12px", background: "none", border: "none", color: tab === n.id ? C.teal : C.textSub, cursor: "pointer", position: "relative", gap: 4 }}>
              <Ic n={n.icon} s={22} />
              <span style={{ fontSize: 10, fontWeight: tab === n.id ? 700 : 400, letterSpacing: .3, fontFamily: F.body }}>{n.label}</span>
              {tab === n.id && <div style={{ position: "absolute", top: 0, left: "20%", right: "20%", height: 2, background: C.teal, borderRadius: "0 0 2px 2px" }} />}
            </button>
          ))}
          <a href={FIND_CLIENTS_URL} target="_blank" rel="noopener noreferrer"
            style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "10px 0 12px", background: "none", border: "none", color: C.textSub, cursor: "pointer", position: "relative", gap: 4, textDecoration: "none" }}>
            <Ic n="search" s={22} />
            <span style={{ fontSize: 10, fontWeight: 400, letterSpacing: .3, fontFamily: F.body }}>Find</span>
          </a>
        </div>
      </div>
      {sharedModals}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CALL LOG — shared, live-updating list of businesses to call
// ═══════════════════════════════════════════════════════════════════════════════
const CALL_LOG_COLORS = ["#3b82f6", "#f97316", "#a855f7", "#ec4899", "#14b8a6", "#eab308", "#ef4444", "#10b981", "#6366f1", "#f43f5e"];
function repLogColor(name) {
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return CALL_LOG_COLORS[Math.abs(hash) % CALL_LOG_COLORS.length];
}

const CALL_OUTCOMES = ["No Answer", "Message Left", "Answer", "Not Interested"];

function isSameLocalDay(isoStr, ref) {
  const d = new Date(isoStr);
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth() && d.getDate() === ref.getDate();
}

function isSameLocalWeek(isoStr, weekStart) {
  const d = new Date(isoStr);
  return d >= weekStart;
}

function isSameLocalMonth(isoStr, ref) {
  const d = new Date(isoStr);
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
}

const normalizeKey = (k) => (k || "").toString().trim().toLowerCase().replace(/[^a-z]/g, "");

function BacklogModal({ currentUserName, isAdmin, isMobile, onClose, onPulled }) {
  const [categories, setCategories]   = useState([]);
  const [items, setItems]             = useState([]); // all backlog items, across categories
  const [loading, setLoading]         = useState(true);
  const [selectedCat, setSelectedCat] = useState(null); // category object or null
  const [newCatName, setNewCatName]   = useState("");
  const [newItemCompany, setNewItemCompany] = useState("");
  const [newItemPhone, setNewItemPhone]     = useState("");
  const [newItemLocation, setNewItemLocation] = useState("");
  const [importing, setImporting]     = useState(false);
  const [importMsg, setImportMsg]     = useState("");
  const [pulledMsg, setPulledMsg]     = useState("");
  const fileInputRef = useRef(null);

  const load = async () => {
    const [cats, its] = await Promise.all([
      sb.select("backlog_categories", "", "name.asc"),
      sb.select("backlog_items", "", "company_name.asc"),
    ]);
    if (cats) setCategories(cats);
    if (its) setItems(its);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const countFor = (catId) => items.filter(i => i.category_id === catId && !i.added_by).length;
  const catItems = selectedCat ? items.filter(i => i.category_id === selectedCat.id) : [];

  const createCategory = async () => {
    if (!newCatName.trim()) return;
    const cat = { id: uid(), name: newCatName.trim(), created_by: currentUserName, created_at: new Date().toISOString() };
    setCategories(prev => [...prev, cat].sort((a, b) => a.name.localeCompare(b.name)));
    setNewCatName("");
    await sb.upsert("backlog_categories", cat);
  };

  const deleteCategory = async (cat) => {
    setCategories(prev => prev.filter(c => c.id !== cat.id));
    setItems(prev => prev.filter(i => i.category_id !== cat.id));
    if (selectedCat?.id === cat.id) setSelectedCat(null);
    await sb.delete("backlog_categories", { id: cat.id }); // items cascade-delete in the DB
  };

  const addItem = async () => {
    if (!newItemCompany.trim() || !selectedCat) return;
    const item = { id: uid(), category_id: selectedCat.id, company_name: newItemCompany.trim(), phone_number: newItemPhone.trim(), location: newItemLocation.trim() || null };
    setItems(prev => [...prev, item]);
    setNewItemCompany(""); setNewItemPhone(""); setNewItemLocation("");
    await sb.upsert("backlog_items", item);
  };

  const deleteItem = async (item) => {
    setItems(prev => prev.filter(i => i.id !== item.id));
    await sb.delete("backlog_items", { id: item.id });
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCat) return;
    setImporting(true);
    setImportMsg("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      const newItems = json.map(r => {
        const entry = {};
        Object.keys(r).forEach(k => { entry[normalizeKey(k)] = r[k]; });
        const company = (entry.companyname || entry.company || "").toString().trim();
        const phone = (entry.phonenumber || entry.phone || "").toString().trim();
        const location = (entry.location || "").toString().trim();
        return company ? { id: uid(), category_id: selectedCat.id, company_name: company, phone_number: phone, location: location || null } : null;
      }).filter(Boolean);

      if (!newItems.length) {
        setImportMsg("No valid rows found — make sure the file has a \"Company Name\" column.");
      } else {
        setItems(prev => [...prev, ...newItems]);
        await sb.upsert("backlog_items", newItems);
        setImportMsg(`Imported ${newItems.length} ${newItems.length === 1 ? "company" : "companies"}.`);
      }
    } catch (err) {
      console.error("Backlog import error:", err);
      setImportMsg("Something went wrong reading that file. Make sure it's a .xlsx, .xls, or .csv file.");
    }
    setImporting(false);
    e.target.value = "";
  };

  const pullItem = async (item) => {
    if (item.added_by) return; // already added — button is disabled, but guard anyway
    const row = {
      id: uid(), company_name: item.company_name, phone_number: item.phone_number || "",
      location: item.location || null,
      checked: false, checked_by: null, checked_at: null, outcome: null, cleared: false,
      created_by: currentUserName,
    };
    const patch = { added_by: currentUserName, added_at: new Date().toISOString() };
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, ...patch } : i)); // stays visible, marked added
    await sb.upsert("call_log", row);
    await sb.update("backlog_items", { id: item.id }, patch);
    onPulled?.();
    setPulledMsg(`Added "${item.company_name}" to your Call Log.`);
    setTimeout(() => setPulledMsg(""), 2500);
  };

  return (
    <Modal title={selectedCat ? selectedCat.name : "Backlogged Call Lists"} onClose={onClose} isMobile={isMobile}>
      {loading ? (
        <div style={{ padding: 20, textAlign: "center", color: C.textMuted, fontSize: 13, fontFamily: F.body }}>Loading…</div>
      ) : !selectedCat ? (
        <>
          <div style={{ fontSize: 12, color: C.textMuted, fontFamily: F.body, marginBottom: 14 }}>
            Pre-built lists of companies to call. Open a category and add whichever ones you want to your own Call Log.
          </div>
          {isAdmin && (
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="New category name" style={{ ...inp, flex: 1 }} onKeyDown={e => e.key === "Enter" && createCategory()} />
              <button onClick={createCategory} style={{ background: C.teal, border: "none", borderRadius: 9, color: "#000", padding: "0 16px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: F.body, whiteSpace: "nowrap" }}>+ Add</button>
            </div>
          )}
          {categories.length === 0 ? (
            <div style={{ padding: "30px 10px", textAlign: "center", color: C.textMuted, fontSize: 13, fontFamily: F.body }}>
              {isAdmin ? "No categories yet — create one above." : "No backlogged lists yet. Check back later."}
            </div>
          ) : categories.map(cat => (
            <div key={cat.id} onClick={() => setSelectedCat(cat)}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 8, cursor: "pointer", background: C.surface }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: F.head }}>{cat.name}</div>
                <div style={{ fontSize: 12, color: C.textMuted, fontFamily: F.body }}>{countFor(cat.id)} {countFor(cat.id) === 1 ? "company" : "companies"}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {isAdmin && (
                  <button onClick={e => { e.stopPropagation(); deleteCategory(cat); }} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", display: "flex" }}><Ic n="x" s={14} /></button>
                )}
                <Ic n="external" s={13} />
              </div>
            </div>
          ))}
        </>
      ) : (
        <>
          <button onClick={() => setSelectedCat(null)} style={{ background: "none", border: "none", color: C.teal, fontSize: 12, fontFamily: F.body, cursor: "pointer", padding: 0, marginBottom: 14, display: "flex", alignItems: "center", gap: 4 }}>
            ← All categories
          </button>

          {isAdmin && (
            <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                <input value={newItemCompany} onChange={e => setNewItemCompany(e.target.value)} placeholder="Company name" style={{ ...inp, flex: 2, minWidth: isMobile ? "100%" : "auto" }} onKeyDown={e => e.key === "Enter" && addItem()} />
                <input value={newItemPhone} onChange={e => setNewItemPhone(e.target.value)} placeholder="Phone number" style={{ ...inp, flex: 1 }} onKeyDown={e => e.key === "Enter" && addItem()} />
                <input value={newItemLocation} onChange={e => setNewItemLocation(e.target.value)} placeholder="Location (optional)" style={{ ...inp, flex: 1 }} onKeyDown={e => e.key === "Enter" && addItem()} />
                <button onClick={addItem} style={{ background: C.teal, border: "none", borderRadius: 9, color: "#000", padding: "0 16px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: F.body, whiteSpace: "nowrap" }}>+ Add</button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button onClick={() => fileInputRef.current?.click()} disabled={importing}
                  style={{ background: "#f1f5f9", border: `1px solid ${C.border2}`, borderRadius: 9, color: C.textSub, padding: "8px 14px", fontWeight: 700, fontSize: 12, cursor: importing ? "default" : "pointer", fontFamily: F.body, display: "flex", alignItems: "center", gap: 6, opacity: importing ? 0.6 : 1 }}>
                  <Ic n="upload" s={13} /> {importing ? "Importing…" : "Import"}
                </button>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} style={{ display: "none" }} />
                {importMsg && <span style={{ fontSize: 11, color: C.textMuted, fontFamily: F.body }}>{importMsg}</span>}
              </div>
            </div>
          )}

          {pulledMsg && <div style={{ fontSize: 12, color: C.green, fontFamily: F.body, marginBottom: 10 }}>{pulledMsg}</div>}

          {catItems.length === 0 ? (
            <div style={{ padding: "30px 10px", textAlign: "center", color: C.textMuted, fontSize: 13, fontFamily: F.body }}>
              {isAdmin ? "No companies in this category yet — add some above." : "Nothing here yet."}
            </div>
          ) : catItems.map(item => (
            <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 6, opacity: item.added_by ? 0.55 : 1 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: F.body, textDecoration: item.added_by ? "line-through" : "none" }}>{item.company_name}</div>
                {item.phone_number && <div style={{ fontSize: 12, color: C.textMuted, fontFamily: F.body, textDecoration: item.added_by ? "line-through" : "none" }}>{item.phone_number}</div>}
                {item.location && <div style={{ fontSize: 12, color: C.textMuted, fontFamily: F.body, textDecoration: item.added_by ? "line-through" : "none" }}>📍 {item.location}</div>}
                {item.added_by && <div style={{ fontSize: 11, color: C.textMuted, fontFamily: F.body, marginTop: 2 }}>Added by {item.added_by}</div>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button onClick={() => pullItem(item)} disabled={!!item.added_by}
                  style={{ background: item.added_by ? "#e8e8e8" : C.tealDim, border: `1px solid ${item.added_by ? C.border2 : C.tealBdr}`, borderRadius: 8, color: item.added_by ? C.textMuted : C.teal, padding: "7px 12px", fontWeight: 700, fontSize: 12, cursor: item.added_by ? "default" : "pointer", fontFamily: F.body, whiteSpace: "nowrap" }}>
                  {item.added_by ? "✓ Added" : "+ Add to My List"}
                </button>
                {isAdmin && <button onClick={() => deleteItem(item)} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", display: "flex" }}><Ic n="x" s={14} /></button>}
              </div>
            </div>
          ))}
        </>
      )}
    </Modal>
  );
}

function CallLogView({ currentUserName, reps, isMobile, onAddToCRM, isAdmin = false }) {
  const [rows, setRows]         = useState([]);
  const [monthRows, setMonthRows] = useState([]); // this month's checked calls, including ones already cleared from the main list
  const [loading, setLoading]   = useState(true);
  const [addModal, setAddModal] = useState(false);
  const [newCompany, setNewCompany] = useState("");
  const [newPhone, setNewPhone]     = useState("");
  const [newLocation, setNewLocation] = useState("");
  const [outcomeRow, setOutcomeRow] = useState(null); // row currently being logged
  const [importing, setImporting]   = useState(false);
  const [importMsg, setImportMsg]   = useState("");
  const [backlogOpen, setBacklogOpen] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    const data = await sb.select("call_log", "cleared=eq.false", "created_at.desc");
    if (data) setRows(isAdmin ? data : data.filter(r => r.created_by === currentUserName));

    // "Calls This Month" needs to include calls already cleared from the
    // visible list (cleared 14 days after being checked, which can be
    // before the month is over), so this is fetched separately and isn't
    // subject to the cleared filter above.
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    const monthData = await sb.select("call_log", `checked=eq.true&checked_at=gte.${monthStart.toISOString()}`, null);
    if (monthData) setMonthRows(isAdmin ? monthData : monthData.filter(r => r.created_by === currentUserName));

    setLoading(false);
  };

  useEffect(() => {
    load();
    // Poll every 5s so everyone sees new/checked-off entries from other reps
    // without needing to refresh — this is a shared, live-updating list.
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addRow = async () => {
    if (!newCompany.trim()) return;
    const row = {
      id: uid(),
      company_name: newCompany.trim(),
      phone_number: newPhone.trim(),
      location: newLocation.trim() || null,
      checked: false,
      checked_by: null,
      checked_at: null,
      outcome: null,
      cleared: false,
      created_by: currentUserName,
    };
    setRows(prev => [row, ...prev]); // optimistic
    setNewCompany(""); setNewPhone(""); setNewLocation(""); setAddModal(false);
    await sb.upsert("call_log", row);
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportMsg("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const newRows = json
        .map(r => {
          const entry = {};
          Object.keys(r).forEach(k => { entry[normalizeKey(k)] = r[k]; });
          const company = (entry.companyname || entry.company || "").toString().trim();
          const phone = (entry.phonenumber || entry.phone || "").toString().trim();
          const location = (entry.location || "").toString().trim();
          return company ? {
            id: uid(),
            company_name: company,
            phone_number: phone,
            location: location || null,
            checked: false,
            checked_by: null,
            checked_at: null,
            outcome: null,
            cleared: false,
            created_by: currentUserName,
          } : null;
        })
        .filter(Boolean);

      if (!newRows.length) {
        setImportMsg("No valid rows found — make sure the file has a \"Company Name\" column.");
      } else {
        setRows(prev => [...newRows, ...prev]); // optimistic
        await sb.upsert("call_log", newRows);
        setImportMsg(`Imported ${newRows.length} ${newRows.length === 1 ? "company" : "companies"}.`);
      }
    } catch (err) {
      console.error("Import error:", err);
      setImportMsg("Something went wrong reading that file. Make sure it's a .xlsx, .xls, or .csv file.");
    }
    setImporting(false);
    e.target.value = ""; // allow re-selecting the same file later
  };

  const downloadTemplate = () => {
    const csv = ["Company Name,Phone Number,Location", 'Example Roofing Co.,(916) 555-0123,"Sacramento, CA"'].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "call-log-import-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Checking the box opens the outcome popup instead of toggling directly —
  // unchecking (undoing a call that was logged by mistake) stays instant.
  const onCheckboxClick = (row) => {
    if (row.checked) {
      const patch = { checked: false, checked_by: null, checked_at: null, outcome: null };
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, ...patch } : r));
      sb.update("call_log", { id: row.id }, patch);
    } else {
      setOutcomeRow(row);
    }
  };

  const logOutcome = async (outcome) => {
    if (!outcomeRow) return;
    const patch = { checked: true, checked_by: currentUserName, checked_at: new Date().toISOString(), outcome };
    setRows(prev => prev.map(r => r.id === outcomeRow.id ? { ...r, ...patch } : r)); // optimistic
    await sb.update("call_log", { id: outcomeRow.id }, patch);
    setOutcomeRow(null);
  };

  const addToCRMFromOutcome = async () => {
    if (!outcomeRow) return;
    onAddToCRM?.(outcomeRow);
    await logOutcome("Added to CRM");
  };

  const deleteRow = async (id) => {
    setRows(prev => prev.filter(r => r.id !== id));
    await sb.delete("call_log", { id });
  };

  // Reps can only delete a line item they personally added. Admin can delete anything.
  const canDelete = (row) => isAdmin || row.created_by === currentUserName;

  const exportReport = () => {
    const monthName = new Date().toLocaleString([], { month: "long", year: "numeric" });
    const doc = new jsPDF();

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    const fmtShort = (d) => d.toLocaleDateString([], { month: "short", day: "numeric" });
    const weekRangeLabel = `${fmtShort(weekStart)} – ${fmtShort(weekEnd)}, ${weekEnd.getFullYear()}`;

    doc.setFontSize(16);
    doc.setTextColor(20, 20, 20);
    doc.text("Call Log — Monthly Report", 14, 18);
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text(`${monthName}  ·  Generated ${new Date().toLocaleDateString()}`, 14, 25);

    autoTable(doc, {
      startY: 32,
      head: [
        ["Sales Rep", "Calls This Month", "Calls This Week"],
        ["", monthName, weekRangeLabel],
      ],
      body: repStats.map(({ name, month, week }) => [name, String(month), String(week)]),
      headStyles: { fillColor: [62, 164, 177], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [245, 246, 247] },
      styles: { fontSize: 11, cellPadding: 6 },
      didParseCell: (data) => {
        // Style the subheader row (month/week labels) distinctly from the main header row
        if (data.section === "head" && data.row.index === 1) {
          data.cell.styles.fontSize = 9;
          data.cell.styles.fontStyle = "normal";
          data.cell.styles.fillColor = [90, 180, 192];
        }
      },
    });

    doc.save(`call-log-report-${new Date().toISOString().slice(0, 7)}.pdf`);
  };

  const repNames = [...new Set((reps || []).map(r => r.name).filter(Boolean))];
  const gridCols = isMobile
    ? "28px 1fr 32px"
    : isAdmin
      ? "36px 1.8fr 1.1fr 1.1fr 1.3fr 1.2fr 32px"   // checkbox, company, phone, rep, date, outcome, delete
      : "36px 2fr 1.3fr 1.4fr 1.3fr 32px";           // checkbox, company, phone, date, outcome, delete (no Rep col — it's always you)

  const outcomeStyle = (outcome) => {
    if (!outcome) return { color: C.textMuted, bg: "transparent" };
    if (outcome === "Not Interested") return { color: C.red, bg: C.red + "18" };
    if (outcome === "Answer" || outcome === "Added to CRM") return { color: C.green, bg: C.green + "18" };
    return { color: C.amber, bg: C.amber + "18" }; // No Answer / Message Left
  };

  // Stats: "today" = local calendar day, "this week" = since most recent Sunday 12am local,
  // "this month" = current calendar month
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay());
  weekStart.setHours(0, 0, 0, 0);

  const checkedRows = rows.filter(r => r.checked && r.checked_at && r.checked_by);
  const checkedMonthRows = monthRows.filter(r => r.checked && r.checked_at && r.checked_by);
  const myToday = checkedRows.filter(r => r.checked_by === currentUserName && isSameLocalDay(r.checked_at, now)).length;
  const myWeek  = checkedRows.filter(r => r.checked_by === currentUserName && isSameLocalWeek(r.checked_at, weekStart)).length;
  const repStats = repNames.map(name => ({
    name,
    today: checkedRows.filter(r => r.checked_by === name && isSameLocalDay(r.checked_at, now)).length,
    week:  checkedRows.filter(r => r.checked_by === name && isSameLocalWeek(r.checked_at, weekStart)).length,
    month: checkedMonthRows.filter(r => r.checked_by === name && isSameLocalMonth(r.checked_at, now)).length,
  }));

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 16 : 28, background: C.bg }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4, gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text, fontFamily: F.head, marginBottom: 4 }}>Call Log</div>
          <div style={{ fontSize: 13, color: C.textMuted, fontFamily: F.body }}>Shared across the whole team — check a box once you've made the call.</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => setBacklogOpen(true)} style={{ background: "#f1f5f9", border: `1px solid ${C.border2}`, borderRadius: 9, color: C.textSub, padding: "10px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: F.body, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
              <Ic n="search" s={14} /> Backlogged Call Lists
            </button>
            {isAdmin && (
              <button onClick={exportReport} style={{ background: "#f1f5f9", border: `1px solid ${C.border2}`, borderRadius: 9, color: C.textSub, padding: "10px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: F.body, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
                <Ic n="cloud" s={14} /> Export Report
              </button>
            )}
            <button onClick={() => fileInputRef.current?.click()} disabled={importing}
              style={{ background: "#f1f5f9", border: `1px solid ${C.border2}`, borderRadius: 9, color: C.textSub, padding: "10px 16px", fontWeight: 700, fontSize: 13, cursor: importing ? "default" : "pointer", fontFamily: F.body, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6, opacity: importing ? 0.6 : 1 }}>
              <Ic n="upload" s={14} /> {importing ? "Importing…" : "Import"}
            </button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} style={{ display: "none" }} />
            <button onClick={() => setAddModal(true)} style={{ background: C.teal, border: "none", borderRadius: 9, color: "#000", padding: "10px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: F.body, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
              <Ic n="plus" s={14} /> Call Log Customer
            </button>
          </div>
          <button onClick={downloadTemplate} style={{ background: "none", border: "none", color: C.teal, fontSize: 12, fontFamily: F.body, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
            Download import template
          </button>
          {importMsg && <div style={{ fontSize: 12, color: C.textMuted, fontFamily: F.body, maxWidth: 260, textAlign: "right" }}>{importMsg}</div>}
        </div>
      </div>

      {/* Personal stat box — reps see only their own counts */}
      {!isAdmin && (
        <div style={{ display: "flex", gap: 10, margin: "16px 0 14px", flexWrap: "wrap" }}>
          <div style={{ background: C.tealDim, border: `1px solid ${C.tealBdr}`, borderRadius: 10, padding: "10px 16px", flex: isMobile ? "1 1 45%" : "0 0 auto" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.teal, fontFamily: F.head }}>{myToday}</div>
            <div style={{ fontSize: 11, color: C.textMuted, fontFamily: F.body, textTransform: "uppercase", letterSpacing: 1 }}>Calls today</div>
          </div>
          <div style={{ background: C.tealDim, border: `1px solid ${C.tealBdr}`, borderRadius: 10, padding: "10px 16px", flex: isMobile ? "1 1 45%" : "0 0 auto" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.teal, fontFamily: F.head }}>{myWeek}</div>
            <div style={{ fontSize: 11, color: C.textMuted, fontFamily: F.body, textTransform: "uppercase", letterSpacing: 1 }}>Calls this week</div>
          </div>
        </div>
      )}

      {/* Stats key — admin only, everyone's today/week counts + color legend */}
      {isAdmin && repNames.length > 0 && (
        <div style={{ margin: "16px 0 18px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 60px 60px" : "1fr 100px 100px", padding: "8px 14px", background: C.bg, borderBottom: `1px solid ${C.border}`, fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>
            <span>Rep</span>
            <span style={{ textAlign: "center" }}>Today</span>
            <span style={{ textAlign: "center" }}>This Week</span>
          </div>
          {repStats.map(({ name, today, week }) => (
            <div key={name} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 60px 60px" : "1fr 100px 100px", padding: "9px 14px", alignItems: "center", borderBottom: `1px solid ${C.border}`, fontSize: 13, fontFamily: F.body }}>
              <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: repLogColor(name), display: "inline-block", flexShrink: 0 }} />
                {name}
              </span>
              <span style={{ textAlign: "center", fontWeight: 700, color: C.text }}>{today}</span>
              <span style={{ textAlign: "center", fontWeight: 700, color: C.text }}>{week}</span>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: gridCols, padding: "10px 14px", background: C.bg, borderBottom: `1px solid ${C.border}`, fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, gap: 8 }}>
          <span></span>
          <span>Company</span>
          {isMobile ? null : <span>Phone</span>}
          {isMobile || !isAdmin ? null : <span>Rep</span>}
          {isMobile ? null : <span>Date</span>}
          {isMobile ? null : <span>Outcome</span>}
          <span></span>
        </div>
        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: C.textMuted, fontSize: 13, fontFamily: F.body }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: C.textMuted, fontSize: 13, fontFamily: F.body }}>No calls logged yet — add one above.</div>
        ) : rows.map(row => {
          const os = outcomeStyle(row.outcome);
          return (
          <div key={row.id}>
            <div style={{ display: "grid", gridTemplateColumns: gridCols, padding: "10px 14px", borderBottom: isMobile ? "none" : `1px solid ${C.border}`, alignItems: "center", fontSize: 13, fontFamily: F.body, gap: 8 }}>
              <input type="checkbox" checked={!!row.checked} onChange={() => onCheckboxClick(row)} style={{ width: 16, height: 16, cursor: "pointer" }} />
              <div style={{ overflow: "hidden" }}>
                <div style={{ color: C.text, fontWeight: 600, textDecoration: row.checked ? "line-through" : "none", opacity: row.checked ? 0.55 : 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.company_name}</div>
                {row.location && <div style={{ fontSize: 11, color: C.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📍 {row.location}</div>}
              </div>
              {!isMobile && <span style={{ color: C.textSub }}>{row.phone_number || "—"}</span>}
              {!isMobile && isAdmin && (
                <span style={{ color: row.checked_by ? repLogColor(row.checked_by) : C.textMuted, fontWeight: row.checked_by ? 700 : 400 }}>
                  {row.checked_by || "—"}
                </span>
              )}
              {!isMobile && (
                <span style={{ color: C.textMuted, fontSize: 12 }}>
                  {row.checked_at ? new Date(row.checked_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </span>
              )}
              {!isMobile && (
                <span style={{ fontSize: 11, fontWeight: 700, color: os.color, background: os.bg, padding: row.outcome ? "3px 9px" : 0, borderRadius: 20, width: "fit-content" }}>
                  {row.outcome || "—"}
                </span>
              )}
              {canDelete(row) && <button onClick={() => deleteRow(row.id)} style={{ background: "none", border: "none", color: C.textMuted, cursor: "pointer", display: "flex" }}><Ic n="x" s={14} /></button>}
            </div>
            {isMobile && (
              <div style={{ padding: "0 14px 10px 46px", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", fontSize: 11, color: C.textMuted, fontFamily: F.body, borderBottom: `1px solid ${C.border}` }}>
                <span>{row.phone_number || "—"}</span>
                {row.location && <span>📍 {row.location}</span>}
                {isAdmin && <span style={{ color: row.checked_by ? repLogColor(row.checked_by) : C.textMuted, fontWeight: row.checked_by ? 700 : 400 }}>{row.checked_by || "not called"}</span>}
                {row.checked_at && <span>{new Date(row.checked_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
                {row.outcome && <span style={{ fontWeight: 700, color: os.color, background: os.bg, padding: "3px 9px", borderRadius: 20 }}>{row.outcome}</span>}
              </div>
            )}
          </div>
        );})}
      </div>

      {/* Add customer modal */}
      {addModal && (
        <Modal title="Call Log Customer" onClose={() => setAddModal(false)} isMobile={isMobile}>
          <Fld label="Company Name"><input style={inp} value={newCompany} onChange={e => setNewCompany(e.target.value)} onKeyDown={e => e.key === "Enter" && addRow()} autoFocus /></Fld>
          <Fld label="Phone Number"><input style={inp} value={newPhone} onChange={e => setNewPhone(e.target.value)} onKeyDown={e => e.key === "Enter" && addRow()} /></Fld>
          <Fld label="Location (optional)"><input style={inp} value={newLocation} onChange={e => setNewLocation(e.target.value)} onKeyDown={e => e.key === "Enter" && addRow()} /></Fld>
          <button onClick={addRow} disabled={!newCompany.trim()} style={{ ...btnP, opacity: newCompany.trim() ? 1 : 0.5, cursor: newCompany.trim() ? "pointer" : "default" }}>Add to Call Log</button>
        </Modal>
      )}

      {backlogOpen && (
        <BacklogModal
          currentUserName={currentUserName}
          isAdmin={isAdmin}
          isMobile={isMobile}
          onClose={() => setBacklogOpen(false)}
          onPulled={load}
        />
      )}

      {/* Call outcome popup */}
      {outcomeRow && (
        <Modal title={`Log call: ${outcomeRow.company_name}`} onClose={() => setOutcomeRow(null)} isMobile={isMobile}>
          <div style={{ fontSize: 12, color: C.textMuted, fontFamily: F.body, marginBottom: 14 }}>What happened on this call?</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
            {CALL_OUTCOMES.map(o => (
              <button key={o} onClick={() => logOutcome(o)}
                style={{ background: C.tealDim, border: `1px solid ${C.tealBdr}`, borderRadius: 10, color: C.teal, padding: "14px 8px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: F.body }}>
                {o}
              </button>
            ))}
          </div>
          <button onClick={addToCRMFromOutcome} style={{ ...btnP, marginTop: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Ic n="plus" s={15} /> Add to CRM
          </button>
        </Modal>
      )}
    </div>
  );
}


export default function App() {
  const isMobile = useIsMobile();
  const [currentUser, setCurrentUser] = useState(null);
  const [contacts,    setContacts]    = useState(null);
  const [tasksDone,   setTasksDone]   = useState({});
  const [notes,       setNotes]       = useState({});
  const [users,       setUsers]       = useState(null);
  const [synced,      setSynced]      = useState(false);
  const [events,      setEvents]      = useState([]);

  useEffect(() => {
    (async () => {
      // Load users from Supabase
      const dbUsers = await sb.select("users");
      const loadedUsers = dbUsers || [];
      setUsers(loadedUsers);

      // Restore session from localStorage
      const session = getSession();
      if (session) {
        const user = loadedUsers.find(u => u.username === session.username);
        if (user) setCurrentUser(user);
      }

      // Load contacts
      const dbContacts = await sb.select("contacts");
      setContacts(dbContacts || []);

      // Load tasks done
      const dbTasks = await sb.select("tasks_done", "", null);
      const td = {};
      (dbTasks || []).forEach(t => { td[t.id] = t.done; });
      setTasksDone(td);

      // Load notes grouped by contact
      const dbNotes = await sb.select("notes", "", "ts.asc");
      const n = {};
      (dbNotes || []).forEach(note => {
        if (!n[note.contact_id]) n[note.contact_id] = [];
        n[note.contact_id].push({ id: note.id, text: note.text, author: note.author, ts: note.ts });
      });
      Object.keys(n).forEach(cid => n[cid].sort((a,b) => new Date(b.ts) - new Date(a.ts)));
      setNotes(n);

      // Load events
      const dbEvents = await sb.select("events");
      setEvents(dbEvents || []);

      setSynced(true);
    })();
  }, []);

  // ── Contact operations ────────────────────────────────────────────────────
  const addContact = async (contact) => {
    const row = {
      id:         contact.id,
      name:       contact.name       || "",
      first_name: contact.first_name || "",
      last_name:  contact.last_name  || "",
      company:    contact.company    || "",
      email:      contact.email      || "",
      phone:      contact.phone      || "",
      address:    contact.address    || "",
      type:       contact.type       || "Hot Lead",
      status:     contact.status     || "Active",
      outcome:    contact.outcome    || null,
      owner:      contact.owner      || "",
      tags:       contact.tags       || [],
      created:    new Date().toISOString(),
    };
    const result = await sb.upsert("contacts", [row]);
    console.log("addContact result:", result);
    setContacts(prev => [...(prev || []), row]);
  };

  const updateContact = async (id, patch) => {
    await sb.update("contacts", { id }, patch);
    setContacts(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
  };

  const deleteContact = async (id) => {
    await sb.delete("contacts", { id });
    await sb.delete("notes", { contact_id: id });
    setContacts(prev => prev.filter(c => c.id !== id));
    setNotes(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  // ── User operations ───────────────────────────────────────────────────────
  const addUser = async (user) => {
    const row = {
      id:       user.id,
      username: user.username,
      password: user.password,
      role:     user.role,
      name:     user.name,
      email:    user.email    || "",
      phone:    user.phone    || "",
      created:  new Date().toISOString(),
    };
    const result = await sb.upsert("users", [row]);
    console.log("addUser result:", result);
    if (result) {
      setUsers(prev => [...(prev || []), row]);
    } else {
      console.error("addUser failed — Supabase did not return data");
      // Still update UI optimistically
      setUsers(prev => [...(prev || []), row]);
    }
  };

  const updateUser = async (id, patch) => {
    await sb.update("users", { id }, patch);
    setUsers(prev => prev.map(u => u.id === id ? { ...u, ...patch } : u));
  };

  const deleteUser = async (id) => {
    await sb.delete("users", { id });
    setUsers(prev => prev.filter(u => u.id !== id));
  };

  // ── Task operations ───────────────────────────────────────────────────────
  const toggleTask = async (taskId, contactId) => {
    const newDone = !tasksDone[taskId];
    const newTd = { ...tasksDone, [taskId]: newDone };
    setTasksDone(newTd);
    await sb.upsert("tasks_done", [{ id: taskId, contact_id: contactId, done: newDone }]);
  };

  // ── Note operations ───────────────────────────────────────────────────────
  const addNote = async (contactId, note) => {
    await sb.upsert("notes", [{ id: note.id, contact_id: contactId, text: note.text, author: note.author, ts: note.ts }]);
    setNotes(prev => ({ ...prev, [contactId]: [note, ...(prev[contactId] || [])] }));
  };

  const deleteNote = async (contactId, noteId) => {
    await sb.delete("notes", { id: noteId });
    setNotes(prev => ({ ...prev, [contactId]: (prev[contactId] || []).filter(n => n.id !== noteId) }));
  };

  // ── Event operations ─────────────────────────────────────────────────────
  const addEvent = async (event) => {
    const row = { id: event.id, title: event.title, type: event.type || "Call", date: event.date, time: event.time, assigned_to: event.assigned_to, contact_id: event.contact_id || null, notes: event.notes || "", created_by: event.created_by || "Admin", created: new Date().toISOString() };
    await sb.upsert("events", [row]);
    setEvents(prev => [...prev, row]);
  };

  const updateEvent = async (id, patch) => {
    await sb.update("events", { id }, patch);
    setEvents(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
  };

  const deleteEvent = async (id) => {
    await sb.delete("events", { id });
    setEvents(prev => prev.filter(e => e.id !== id));
  };

  // ── Auth ──────────────────────────────────────────────────────────────────
  const login  = user => { setCurrentUser(user); setSession({ username: user.username }); };
  const logout = ()   => { setCurrentUser(null); setSession(null); };
  const handleLogin = (username, password) => {
    const cleanUser = (username || "").trim().toLowerCase().replace(/\s+/g, "");
    const cleanPass = (password || "").trim();
    const user = (users || []).find(u => u.username.trim().toLowerCase() === cleanUser && u.password === cleanPass);
    if (user) { login(user); return true; }
    return false;
  };

  const globalStyle = `
    @import url('https://fonts.googleapis.com/css2?family=Barlow+Semi+Condensed:wght@400;600;700;800&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    body{background:${C.bg};color:${C.text};font-family:'DIN Alternate','Arial Narrow',sans-serif;-webkit-tap-highlight-color:transparent}
    ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:#f1f5f9}::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:3px}
    select option{background:#fff;color:#0f172a}
    input[type=date]::-webkit-calendar-picker-indicator{filter:none}
    textarea:focus,input:focus,select:focus{outline:none;border-color:#3ea4b144!important}
    button{font-family:'DIN Alternate','Arial Narrow',sans-serif}
  `;

  // Show login page immediately — button will show "Loading..." until users are ready
  if (!currentUser && (contacts === null || users === null)) return (
    <>
      <style>{globalStyle}</style>
      <LoginPage onLogin={handleLogin} usersLoaded={users !== null} />
    </>
  );

  // Full loading screen only if somehow logged in but data not ready
  if (contacts === null || users === null) return (
    <>
      <style>{globalStyle}</style>
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: C.teal, fontFamily: F.body, fontSize: 16, gap: 16 }}>
        <img src={logo} alt="JG Graphics" style={{ width: 70, height: 70, borderRadius: "50%" }} />
        <div>Loading JG Graphics CRM…</div>
      </div>
    </>
  );

  // Shared props for both admin and rep
  const dbOps = { addContact, updateContact, deleteContact, addUser, updateUser, deleteUser, toggleTask, addNote, deleteNote, addEvent, updateEvent, deleteEvent };

  return (
    <>
      <style>{globalStyle}</style>
      {!currentUser && <LoginPage onLogin={handleLogin} usersLoaded={users !== null} />}
      {currentUser?.role === "admin" && (
        <AdminDashboard
          contacts={contacts}
          tasksDone={tasksDone}
          notes={notes}
          users={users}
          events={events}
          onLogout={logout}
          isMobile={isMobile}
          db={dbOps}
          currentUser={currentUser}
        />
      )}
      {currentUser?.role === "rep" && (
        <RepCRM
          currentUser={currentUser}
          allContacts={contacts}
          tasksDone={tasksDone}
          notes={notes}
          events={events}
          users={users}
          onLogout={logout}
          isMobile={isMobile}
          synced={synced}
          db={dbOps}
        />
      )}
    </>
  );
}
